// react-shim.js
import * as React from "react";

// src/components/RecruiterChatView.tsx
import { useEffect as useEffect11, useState as useState12 } from "react";
import { useNavigate, useParams as useParams2 } from "react-router-dom";

// src/context/ChatAppContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect as useEffect3,
  useReducer,
  useRef
} from "react";

// src/models.ts
var ChatMessageStatus = /* @__PURE__ */ ((ChatMessageStatus2) => {
  ChatMessageStatus2["Initial"] = "initial";
  ChatMessageStatus2["Sending"] = "sending";
  ChatMessageStatus2["Sent"] = "sent";
  ChatMessageStatus2["Error"] = "error";
  return ChatMessageStatus2;
})(ChatMessageStatus || {});

// src/context/types.ts
var ActionType = /* @__PURE__ */ ((ActionType2) => {
  ActionType2["SET_USER"] = "SET_USER";
  ActionType2["SEND_MESSAGE"] = "SEND_MESSAGE";
  ActionType2["SEND_MESSAGE_SUCCESS"] = "SEND_MESSAGE_SUCCESS";
  ActionType2["SEND_MESSAGE_ERROR"] = "SEND_MESSAGE_ERROR";
  ActionType2["READ_MESSAGE"] = "READ_MESSAGE";
  ActionType2["UPDATE_CHAT_MESSAGES"] = "UPDATE_CHAT_MESSAGES";
  ActionType2["SET_CHAT_LOADING"] = "SET_CHAT_LOADING";
  ActionType2["SET_CHAT_METADATA"] = "SET_CHAT_METADATA";
  ActionType2["SET_CHAT"] = "SET_CHAT";
  ActionType2["WORKSPACE_UPDATED"] = "WORKSPACE_UPDATED";
  ActionType2["ADD_MESSAGE"] = "ADD_MESSAGE";
  ActionType2["TYPING_STATUS_UPDATE"] = "TYPING_STATUS_UPDATE";
  ActionType2["SET_DRAFT"] = "SET_DRAFT";
  ActionType2["START_POLLING"] = "START_POLLING";
  ActionType2["STOP_POLLING"] = "STOP_POLLING";
  ActionType2["SET_WORKSPACES"] = "SET_WORKSPACES";
  ActionType2["ADD_WORKSPACE"] = "ADD_WORKSPACE";
  ActionType2["SET_AUTH_STATE"] = "SET_AUTH_STATE";
  return ActionType2;
})(ActionType || {});

// src/context/selectors.ts
var workspaceHasMember = (w, userId) => Boolean(w.members?.some((m) => m.id === userId));
var allChatStatesSelector = (appState) => Object.values(appState.chats);
var allChatsSelector = (appState) => Object.values(appState.chats).map((c) => c.chat);
var allChatsFromWorkspacesSelector = (workspaces) => workspaces.reduce((acc, w) => {
  if (w.chats) {
    acc.push(...w.chats.map(mapChatSubject));
  }
  return acc;
}, []);
function mapChatSubject(c) {
  const candidate = c.users?.find((u) => u.role === "candidate");
  return {
    ...c,
    // TODO: the back end should serve both these properties
    subjectUser: candidate,
    subject: c.subject || constructFullName(candidate?.profile) || candidate?.email || "New Chat"
  };
}
var chatSelector = (appState, chatId) => {
  if (!chatId) {
    return void 0;
  }
  const chatState = appState.chats[chatId];
  return chatState?.chat;
};
var getChatUrl = (_appState, chatId) => `/chat/${chatId}`;
var getWorkspaceChat = (workspace, user) => {
  const chat = workspace.chats?.find(
    (c) => c.users?.find((u) => u.id === user?.id)
  );
  return chat;
};
var findWorkspaceById = (appState, workspaceId) => appState.workspaces.find((w) => w.id === workspaceId);
var authStateSelector = (state) => state.authState;
var profileDisplayName = (profile) => {
  return constructFullName(profile ?? {}) || profile?.email || "";
};
var profileLocation = (profile) => {
  return [
    profile?.locality,
    profile?.region,
    profile?.country
  ].filter(Boolean).join(", ");
};
var constructFullName = ({
  firstName,
  lastName
} = {}) => {
  return `${firstName ?? ""} ${lastName ?? ""}`.trim();
};
var externalWorkspacesSelector = (appState) => {
  return appState.workspaces.filter((w) => w.slug !== "betterleap");
};
var chatHasRecruiterReply = (chatState) => {
  return chatState.hasRecruiterReplied === true;
};
var unrepliedChatsSelector = (allChatStates) => {
  return allChatStates.filter((c) => !chatHasRecruiterReply(c));
};
var repliedChatsSelector = (allChatStates) => {
  return allChatStates.filter((c) => chatHasRecruiterReply(c));
};
var unreadChatsSelector = (allChatStates, userId) => {
  return allChatStates.filter(
    (c) => userId && c.unreadCount && c.unreadCount > 0
  );
};
var unreadChatsCount = (appState) => {
  const allChatStates = allChatStatesSelector(appState);
  const unreadChats = unreadChatsSelector(allChatStates, appState.user?.id);
  return unreadChats.length;
};
var unreadMessagesCount = (appState) => {
  const allChatStates = allChatStatesSelector(appState);
  const unreadCount = allChatStates.reduce((acc, chatState) => {
    const unreadCount2 = appState.user?.id ? chatState.unreadCount || 0 : 0;
    return acc + unreadCount2;
  }, 0);
  return unreadCount;
};

// src/context/utils.ts
var replaceDateIfNewer = (date, newDate) => {
  let next = null;
  if (!date && !newDate) {
    return null;
  } else if (!date) {
    next = newDate;
  } else if (!newDate) {
    next = date;
  } else {
    next = new Date(date).getTime() > new Date(newDate).getTime() ? newDate : date;
  }
  return next instanceof Date ? next.toISOString() : next;
};
var replaceDateIfOlder = (date, newDate) => {
  let next = null;
  if (!date && !newDate) {
    return null;
  } else if (!date) {
    next = newDate;
  } else if (!newDate) {
    next = date;
  } else {
    next = new Date(date).getTime() < new Date(newDate).getTime() ? newDate : date;
  }
  return next instanceof Date ? next.toISOString() : next;
};
var isNewer = (date, newDate) => {
  if (!date && !newDate) {
    return false;
  }
  if (date && !newDate) {
    return true;
  }
  if (!date && newDate) {
    return false;
  }
  return new Date(date).getTime() > new Date(newDate).getTime();
};

// src/context/chat-app-reducer.ts
var initialState = {
  config: {
    apiBaseUrl: "http://localhost:4000/api",
    apiNamespace: "candidate"
  },
  authState: "signedOut",
  workspaces: [],
  localLastUpdatedAt: 0,
  remoteLastUpdatedAt: 0,
  didFetchWorkspaces: false,
  chats: {}
};
var chatReducer = (state, action) => {
  switch (action.type) {
    case "SEND_MESSAGE" /* SEND_MESSAGE */:
      return {
        ...state,
        chat: {
          ...state.chat,
          draft: "",
          inflightMessages: [
            ...state.chat.inflightMessages || [],
            {
              id: action.payload._tempId,
              chatId: state.chat.id,
              senderId: action.payload.message.sender?.id,
              sender: action.payload.message.sender,
              content: action.payload.message.content,
              type: "default",
              createdAt: (/* @__PURE__ */ new Date()).toISOString(),
              updatedAt: (/* @__PURE__ */ new Date()).toISOString(),
              status: "sending" /* Sending */,
              userState: [
                {
                  userId: action.payload.message.sender?.id,
                  chatId: state.chat.id,
                  messageId: action.payload._tempId,
                  readAt: (/* @__PURE__ */ new Date()).toISOString()
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }
              ]
            }
          ]
        }
      };
    case "SEND_MESSAGE_SUCCESS" /* SEND_MESSAGE_SUCCESS */:
      return {
        ...state,
        chat: {
          ...state.chat,
          inflightMessages: state.chat.inflightMessages?.filter(
            (msg) => msg.id !== action.payload._tempId
          ),
          status: "sent" /* Sent */
        },
        messages: [
          ...(state.messages || []).filter(
            (msg) => msg.id !== action.payload.message.id
          ),
          {
            ...action.payload.message
          }
        ],
        oldestMessageDate: replaceDateIfOlder(
          state.oldestMessageDate,
          action.payload.message.createdAt
        ),
        newestMessageDate: replaceDateIfNewer(
          state.newestMessageDate,
          action.payload.message.createdAt
        )
      };
    case "ADD_MESSAGE" /* ADD_MESSAGE */:
      if (state.messages?.some((msg) => msg.id === action.payload.message.id)) {
        return state;
      }
      return {
        ...state,
        chat: {
          ...state.chat
        },
        messages: [...state.messages || [], action.payload.message],
        oldestMessageDate: replaceDateIfOlder(
          state.oldestMessageDate,
          action.payload.message.createdAt
        ),
        newestMessageDate: replaceDateIfNewer(
          state.newestMessageDate,
          action.payload.message.createdAt
        )
      };
    case "SEND_MESSAGE_ERROR" /* SEND_MESSAGE_ERROR */:
      return {
        ...state,
        chat: {
          ...state.chat,
          inflightMessages: state.chat.inflightMessages?.map(
            (message) => message.id === action.payload._tempId ? { ...message, status: "error" /* Error */ } : message
          )
        }
      };
    case "SET_DRAFT" /* SET_DRAFT */:
      return {
        ...state,
        chat: {
          ...state.chat,
          draft: action.payload.draft
        }
      };
    case "READ_MESSAGE" /* READ_MESSAGE */:
      return {
        ...state,
        messages: state.messages?.map(
          (message) => message.id === action.payload.messageId ? {
            ...message,
            userState: [
              ...message.userState?.filter(
                (messageUserState) => messageUserState.userId !== action.payload.userId
              ) || [],
              {
                userId: action.payload.userId,
                readAt: action.payload.readAt,
                messageId: message.id
              }
            ]
          } : message
        )
      };
    case "UPDATE_CHAT_MESSAGES" /* UPDATE_CHAT_MESSAGES */:
      const {
        pageInfo: { direction },
        messages: newMessages,
        totalCount,
        oldestMessageDate,
        newestMessageDate
      } = action.payload.data;
      const allMessages = direction === "before" ? [...newMessages, ...state.messages || []] : [...state.messages || [], ...newMessages];
      const messageMap = new Map(
        allMessages.map((message) => [message.id, message])
      );
      const uniqueMessages = Array.from(messageMap.values());
      const sortedMessages = uniqueMessages.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      return {
        ...state,
        oldestMessageDate: replaceDateIfOlder(
          state.oldestMessageDate,
          oldestMessageDate
        ),
        newestMessageDate: replaceDateIfNewer(
          state.newestMessageDate,
          newestMessageDate
        ),
        messages: sortedMessages,
        lastUpdatedAt: (/* @__PURE__ */ new Date()).toISOString(),
        totalCount
      };
    case "SET_CHAT_METADATA" /* SET_CHAT_METADATA */: {
      const metadata = action.payload.metadata ?? {};
      const {
        unreadCount,
        hasRecruiterReplied,
        lastReadAt,
        oldestMessageDate: oldestMessageDate2,
        newestMessageDate: newestMessageDate2
      } = metadata;
      if (isNewer(state.lastReadAt, lastReadAt)) {
        console.warn("Chat metadata is older than the current lastReadAt!?");
        console.log({
          state: state.lastReadAt,
          lastReadAt
        });
        return state;
      }
      return {
        ...state,
        unreadCount,
        hasRecruiterReplied,
        lastReadAt,
        oldestMessageDate: oldestMessageDate2,
        newestMessageDate: newestMessageDate2
      };
    }
    default:
      return state;
  }
};
var appReducer = (state, action) => {
  switch (action.type) {
    case "SEND_MESSAGE" /* SEND_MESSAGE */:
    case "SEND_MESSAGE_SUCCESS" /* SEND_MESSAGE_SUCCESS */:
    case "ADD_MESSAGE" /* ADD_MESSAGE */:
    case "SEND_MESSAGE_ERROR" /* SEND_MESSAGE_ERROR */:
    case "SET_DRAFT" /* SET_DRAFT */:
    case "READ_MESSAGE" /* READ_MESSAGE */:
    case "UPDATE_CHAT_MESSAGES" /* UPDATE_CHAT_MESSAGES */:
    case "SET_CHAT_METADATA" /* SET_CHAT_METADATA */:
      if (!state.chats[action.payload.chatId]) {
        return state;
      }
      return {
        ...state,
        chats: {
          ...state.chats,
          [action.payload.chatId]: chatReducer(
            state.chats[action.payload.chatId],
            action
          )
        }
      };
    case "SET_USER" /* SET_USER */:
      return {
        ...state,
        authState: action.payload.user ? "signedIn" : "signedOut",
        user: action.payload.user,
        claims: action.payload.user ? action.payload.claims : void 0
      };
    case "SET_AUTH_STATE" /* SET_AUTH_STATE */:
      return {
        ...state,
        authState: action.payload
      };
    case "SET_WORKSPACES" /* SET_WORKSPACES */:
      const chats = allChatsFromWorkspacesSelector(action.payload.workspaces);
      return {
        ...state,
        localLastUpdatedAt: Date.now(),
        workspaces: deepMergeWorkspaces(
          state.workspaces,
          action.payload.workspaces
        ),
        didFetchWorkspaces: true,
        chats: deepMergeChatStateWithChats(state.chats, chats)
      };
    case "WORKSPACE_UPDATED" /* WORKSPACE_UPDATED */:
      return {
        ...state,
        remoteLastUpdatedAt: action.payload.updatedAt
        // new Date().toISOString(),
      };
    case "ADD_WORKSPACE" /* ADD_WORKSPACE */:
      if (state.workspaces.some((w) => w.id === action.payload.workspace.id)) {
        return state;
      }
      return {
        ...state,
        workspaces: [...state.workspaces, action.payload.workspace]
      };
    case "TYPING_STATUS_UPDATE" /* TYPING_STATUS_UPDATE */:
      const chatState = state.chats[action.payload.chatId];
      if (!chatState) {
        return state;
      }
      const updatedChat = {
        ...chatState,
        chat: {
          ...chatState.chat,
          typingStatus: action.payload
        }
      };
      return {
        ...state,
        chats: {
          ...state.chats,
          [action.payload.chatId]: updatedChat
        }
      };
    case "SET_CHAT" /* SET_CHAT */:
      return {
        ...state,
        chats: {
          ...state.chats,
          [action.payload.id]: {
            chat: mapChatSubject(action.payload),
            messages: action.payload.messages,
            isLoadingNewer: false,
            isLoadingOlder: false
          }
        }
      };
    default:
      return state;
  }
};
var deepMergeWorkspaces = (oldWorkspaces, newWorkspaces) => {
  const mergedWorkspaces = newWorkspaces.map((newWorkspace) => {
    const oldWorkspace = oldWorkspaces.find((w) => w.id === newWorkspace.id);
    if (!oldWorkspace) {
      return newWorkspace;
    }
    return {
      ...oldWorkspace,
      ...newWorkspace
    };
  });
  return mergedWorkspaces;
};
var deepMergeChatStateWithChats = (chatState, chats) => {
  const newChatState = {};
  chats.forEach((chat) => {
    const state = chatState[chat.id] || {
      isLoadingOlder: false,
      isLoadingNewer: false
    };
    newChatState[chat.id] = {
      ...state,
      chat: {
        ...state?.chat,
        ...chat
      }
    };
  });
  return {
    ...chatState,
    ...newChatState
  };
};

// src/hooks/useInitWorkspaces.ts
import { useEffect, useState } from "react";
import { unstable_batchedUpdates as batchedUpdates } from "react-dom";

// src/apiClient.ts
import { ApiClient } from "@betterleap/client";
import { BetterleapHttpRequest, TOKEN, getEnvVar } from "@betterleap/shared";
var resolvedApiBaseUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || getEnvVar("REACT_APP_API_URL") || getEnvVar("NEXT_PUBLIC_API_URL") || getEnvVar("VITE_API_URL") || "").replace("/api", "");
var apiClientConfig = {
  BASE: resolvedApiBaseUrl,
  TOKEN: async () => TOKEN.get() ?? "",
  WITH_CREDENTIALS: true
};
var apiClient = new ApiClient(apiClientConfig, BetterleapHttpRequest);

// src/hooks/useInitWorkspaces.ts
function useInitWorkspaces() {
  const appCtx = useChatAppContext();
  const authState = appCtx.state.authState;
  const enabled = appCtx.enabled && authState === "signedIn";
  const { user, localLastUpdatedAt, remoteLastUpdatedAt } = appCtx.state;
  const [didFetchWorkspaces, setDidFetchWorkspaces] = useState(false);
  const fetchWorkspaces = async () => {
    try {
      if (!enabled) {
        return;
      }
      let response;
      if (appCtx.state.config.apiNamespace === "candidate") {
        response = await apiClient.chat.getWorkspaces();
      } else {
        response = await apiClient.chatWorkspace.getOrgDefaultWorkspace();
      }
      const data = response.data;
      if (data) {
        batchedUpdates(() => {
          appCtx.dispatch({
            type: "SET_WORKSPACES" /* SET_WORKSPACES */,
            payload: {
              workspaces: data.workspaces
            }
          });
          if ("metadata" in data && Array.isArray(data.metadata)) {
            data.metadata.forEach((md) => {
              appCtx.dispatch({
                type: "SET_CHAT_METADATA" /* SET_CHAT_METADATA */,
                payload: {
                  chatId: md.chatId,
                  metadata: md
                }
              });
            });
          }
          if (!user) {
            appCtx.dispatch({
              type: "SET_USER" /* SET_USER */,
              payload: {
                user: data.user
              }
            });
          }
          setDidFetchWorkspaces(true);
        });
      }
    } catch (error) {
      appCtx.dispatch({
        type: "SET_AUTH_STATE" /* SET_AUTH_STATE */,
        payload: "error"
      });
      appCtx.showGlobalError("Unable to load chat workspace. Please retry.");
      console.error(error);
    } finally {
      setDidFetchWorkspaces(true);
    }
  };
  useEffect(() => {
    fetchWorkspaces();
  }, [enabled]);
  useEffect(() => {
    if (localLastUpdatedAt < remoteLastUpdatedAt && user) {
      console.log("Local workspaces are outdated, fetching new workspaces. ", {
        localLastUpdatedAt,
        remoteLastUpdatedAt
      });
      fetchWorkspaces();
    }
  }, [localLastUpdatedAt, remoteLastUpdatedAt]);
  return {
    didFetchWorkspaces
  };
}

// src/hooks/useChatGateway/index.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
import { io } from "socket.io-client";
import { getAuth } from "firebase/auth";
var IS_DEV = process.env.NODE_ENV === "development";
var socketGatewayId = null;
var socketHandle = null;
var connected = false;
var connectionAttempted = false;
var gatewayIdInterceptor = (_event, data) => {
  if ("__gatewayId" in data) {
    const { __gatewayId } = data;
    if (!socketGatewayId) {
      socketGatewayId = __gatewayId;
      console.info("Socket Gateway ID set to: ", socketGatewayId);
    } else if (socketGatewayId !== __gatewayId) {
      console.error(
        `Gateway ID mismatch! Expected: ${socketGatewayId}, got: ${__gatewayId}`
      );
    }
  }
};
function useChatGateway(ctx) {
  const [token, setToken] = useState2(null);
  const url = new URL("/", ctx.state.config.apiBaseUrl);
  const onMessageHandler = (data) => {
    const { message, workspaceId } = data;
    ctx.dispatch({
      type: "ADD_MESSAGE" /* ADD_MESSAGE */,
      payload: {
        chatId: message.chatId,
        workspaceId,
        message
      }
    });
  };
  const onReadHandler = (data) => {
    const { messageId, readAt, userId, workspaceId, chatId } = data;
    ctx.dispatch({
      type: "READ_MESSAGE" /* READ_MESSAGE */,
      payload: {
        chatId,
        messageId,
        readAt,
        userId,
        workspaceId
      }
    });
  };
  const onTypingHandler = (data) => {
    ctx.dispatch({
      type: "TYPING_STATUS_UPDATE" /* TYPING_STATUS_UPDATE */,
      payload: data
    });
  };
  const onWorkspaceUpdatedHandler = (data) => {
    const { updatedAt } = data;
    ctx.dispatch({
      type: "WORKSPACE_UPDATED" /* WORKSPACE_UPDATED */,
      payload: {
        updatedAt
      }
    });
  };
  const onMetadataHandler = (_eventName, data) => {
    const chatId = typeof data === "object" && data && "chatId" in data && typeof data.chatId === "string" ? data.chatId : void 0;
    if (chatId && typeof data === "object" && data && "metadata" in data) {
      const metadata = data.metadata;
      ctx.dispatch({
        type: "SET_CHAT_METADATA" /* SET_CHAT_METADATA */,
        payload: {
          chatId,
          metadata
        }
      });
    }
  };
  const connectSocket = async () => {
    console.log("useChatGateway - Attempting connection...");
    if (socketHandle || connectionAttempted) {
      console.warn(
        "useChatGateway - Socket already connected or in progress..."
      );
      return;
    }
    if (!token) {
      console.error(
        "useChatGateway - No token found, cannot connect to ChatGateway."
      );
      return;
    }
    console.log("useChatGateway - Connecting with token: ", token);
    connectionAttempted = true;
    const socket = io(url.toString(), {
      auth: {
        token
      },
      transports: ["websocket"]
    });
    const callback = () => {
      console.log("useChatGateway - Socket connected? ", socket.connected);
      connected = socket.connected;
      connectionAttempted = false;
      if (!socket.connected) {
        socketGatewayId = null;
      }
    };
    socketHandle = socket;
    socket.on("connect", callback);
    socket.on("disconnect", callback);
    socket.on("error", onError);
    socket.onAny(gatewayIdInterceptor);
    socket.onAny(onMetadataHandler);
    socket.on("chat:message" /* MESSAGE */, onMessageHandler);
    socket.on("chat:message:read" /* READ */, onReadHandler);
    socket.on("chat:user:typing" /* TYPING */, onTypingHandler);
    socket.on("workspace:updated" /* WORKSPACE_UPDATED */, onWorkspaceUpdatedHandler);
  };
  const disconnectSocket = () => {
    if (!socketHandle) {
      return;
    }
    console.warn("useChatGateway - Disconnecting...");
    socketHandle.off("chat:message" /* MESSAGE */, onMessageHandler);
    socketHandle.off("chat:message:read" /* READ */, onReadHandler);
    socketHandle.off("chat:user:typing" /* TYPING */, onTypingHandler);
    socketHandle.off(
      "workspace:updated" /* WORKSPACE_UPDATED */,
      onWorkspaceUpdatedHandler
    );
    socketHandle.offAny(gatewayIdInterceptor);
    socketHandle.offAny(onMetadataHandler);
    socketHandle.disconnect();
    connected = false;
    connectionAttempted = false;
    socketHandle = null;
  };
  const onTokenRefresh = async (user) => {
    if (!user) {
      console.log(
        "useChatGateway - onIdTokenChanged: No user found, clearing token."
      );
      setToken(null);
      return;
    }
    const userToken = await user.getIdToken();
    setToken(userToken);
    if (token && socketHandle) {
      console.log(
        "useChatGateway:onIdTokenChanged - Token changed, refreshing connection..."
      );
    }
  };
  const onError = (error) => {
    console.error("useChatGateway - Socket error: ", error);
  };
  useEffect2(() => {
    async function initialToken() {
      const user = getAuth().currentUser;
      if (user) {
        console.log("useChatGateway - Initial token found.");
        const userToken = await user.getIdToken();
        setToken(userToken);
      }
    }
    initialToken();
    return getAuth().onIdTokenChanged((user) => onTokenRefresh(user));
  }, []);
  useEffect2(() => {
    if (!ctx.enabled || ctx.state.authState !== "signedIn") {
      return;
    }
    if (token) {
      connectSocket();
    }
    return () => {
      disconnectSocket();
    };
  }, [ctx.enabled, ctx.state.authState, token]);
  const sendEvent = (eventName, data) => {
    return new Promise((resolve, reject) => {
      if (!socketHandle) {
        reject("Socket not connected - " + eventName);
        return;
      }
      if (ctx.state.claims?.isSuperAdmin) {
        return;
      }
      if (IS_DEV) {
        console.log("Sending event: ", eventName, data);
      }
      socketHandle.emit(eventName, data, (response) => {
        resolve(response);
      });
    });
  };
  const sendMessageAndWait = async (workspaceId, chatId, message, attachmentIds, page) => {
    return sendEvent("chat:message" /* MESSAGE */, {
      workspaceId,
      chatId,
      message,
      attachmentIds,
      page
    });
  };
  const markMessageAsRead = async (messageId, chatId) => {
    const res = await sendEvent(
      "chat:message:read" /* READ */,
      {
        chatId,
        messageId
      }
    );
    onMetadataHandler("__metadata__", res);
    return res;
  };
  const setIsTyping = (isTyping, chatId) => {
    return sendEvent("chat:user:typing" /* TYPING */, {
      isTyping,
      chatId,
      lastUpdated: Date.now()
    });
  };
  return {
    connected,
    sendMessageAndWait,
    markMessageAsRead,
    setIsTyping
  };
}

// src/context/ChatAppContext.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var ChatAppContext = createContext({
  showGlobalError: () => {
  },
  showToast: () => {
  },
  getUserToken: () => Promise.resolve(""),
  state: initialState,
  dispatch: () => Promise.resolve(),
  gateway: {
    connected: false,
    setIsTyping: () => Promise.resolve({}),
    sendMessageAndWait: () => Promise.resolve({}),
    markMessageAsRead: () => Promise.resolve({})
  },
  enabled: true
});
var InitWorkspaces = ({ children }) => {
  useInitWorkspaces();
  return /* @__PURE__ */ jsx(Fragment, { children });
};
var ChatAppContextProvider = ({
  children,
  initialState: initialState2,
  getUserToken,
  showGlobalError,
  showToast: showToast4,
  enabled = true
}) => {
  const [state, dispatch] = useReducer(appReducer, {
    ...initialState,
    ...initialState2
  });
  const stateRef = useRef(state);
  useEffect3(() => {
    stateRef.current = state;
  }, [state]);
  const getState = useCallback(() => stateRef.current, []);
  const enhancedDispatch = useCallback(
    async (action) => {
      if (typeof action === "function") {
        return action(dispatch, getState);
      } else {
        return dispatch(action);
      }
    },
    [dispatch, getState]
  );
  const context = {
    state,
    dispatch: enhancedDispatch,
    getUserToken,
    showGlobalError,
    showToast: showToast4,
    enabled
  };
  const gateway = useChatGateway(context);
  return /* @__PURE__ */ jsx(
    ChatAppContext.Provider,
    {
      value: {
        ...context,
        gateway
      },
      children: /* @__PURE__ */ jsx(InitWorkspaces, { children })
    }
  );
};
var useChatAppContext = () => useContext(ChatAppContext);
var useChatState = () => useChatAppContext().state;

// src/utils.ts
import { groupBy, mapValues } from "lodash";
import { format, isThisYear, isToday, isYesterday } from "date-fns";
import { showToast } from "@betterleap/ui";
function userHasReadMessage(message, userId) {
  return Boolean(
    message.userState?.some((state) => {
      return state.userId === userId && !!state.readAt;
    })
  );
}
function sortChatStatesByMostRecent(chatStates) {
  const sorter = (a, b) => {
    if (a.newestMessageDate && b.newestMessageDate) {
      return a.newestMessageDate > b.newestMessageDate ? -1 : 1;
    }
    return new Date(b.newestMessageDate || b.chat.createdAt).getTime() - new Date(a.newestMessageDate || a.chat.createdAt).getTime();
  };
  if (!chatStates) {
    return [];
  }
  return chatStates.slice().sort(sorter);
}
function sortChatsByMostRecent(chats) {
  const sorter = (a, b) => {
    const aMessages = a.messages || [];
    const bMessages = b.messages || [];
    const aCreatedAt = aMessages[aMessages.length - 1]?.createdAt || a.createdAt;
    const bCreatedAt = bMessages[bMessages.length - 1]?.createdAt || b.createdAt;
    if (aCreatedAt > bCreatedAt) return -1;
    if (aCreatedAt < bCreatedAt) return 1;
    return 0;
  };
  if (!chats) {
    return [];
  }
  return chats.slice().sort(sorter);
}
var getWorkspaceAvatar = ({
  workspace,
  user,
  subject
}) => {
  if (user?.role === "recruiter") {
    return getSenderAvatar({ sender: subject });
  } else {
    return getSenderAvatar({ workspace });
  }
};
var getSenderAvatar = ({
  workspace,
  sender
}) => {
  switch (sender?.role) {
    case "candidate":
      return sender?.imageUrl;
    default:
      return workspace?.avatarUrl;
  }
};
function formatDate(date) {
  if (isToday(date)) {
    return "Today";
  } else if (isYesterday(date)) {
    return "Yesterday";
  } else if (isThisYear(date)) {
    return format(date, "MMMM d");
  } else {
    return format(date, "MMMM d, yyyy");
  }
}
var getMessageBreaks = (messages, userId) => {
  let lastDate = void 0;
  let firstUnreadId = void 0;
  const dateBreaks = {};
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const date = formatDate(new Date(message.createdAt));
    if (date !== lastDate) {
      dateBreaks[message.id] = date;
      lastDate = date;
    }
    if (!firstUnreadId && message.senderId !== userId && !userHasReadMessage(message, userId)) {
      firstUnreadId = message.id;
    }
  }
  return { dateBreaks, firstUnreadId };
};
var groupByDateAndAuthor = (messages) => {
  const messagesByDate = groupBy(messages, (message) => {
    return formatDate(new Date(message.createdAt));
  });
  return mapValues(
    messagesByDate,
    (messages2) => groupMessagesByAuthor(messages2)
  );
};
var groupMessagesByAuthor = (messages) => {
  if (!messages.length) return [];
  let grouped = [];
  let lastAuthor = messages[0].senderId;
  let authorMessages = [messages[0]];
  for (let i = 1; i < messages.length; i++) {
    const message = messages[i];
    if (message.senderId !== lastAuthor) {
      grouped.push(authorMessages);
      authorMessages = [message];
    } else {
      authorMessages.push(message);
    }
    lastAuthor = message.senderId;
  }
  if (authorMessages.length) {
    grouped.push(authorMessages);
  }
  return grouped;
};
var VALID_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/doc",
  "application/docx",
  "text/plain"
];
var MAX_FILE_SIZE = 2 * 1024 * 1024;
var validateFile = (file) => {
  if (!VALID_FILE_TYPES.includes(file.type)) {
    showToast({
      title: "Invalide file type",
      description: `Unsupported file type: ${file.type}.`,
      variant: "danger"
    });
    return { isValid: false, errorMessage: "Unsupported file type" };
  }
  if (file.size > MAX_FILE_SIZE) {
    showToast({
      title: "File is too large",
      description: `File size exceeds 2MB limit.`,
      variant: "danger"
    });
    return { isValid: false, errorMessage: "File is too large" };
  }
  return { isValid: true };
};
var checkAndUploadFiles = (fileList, uploadFiles) => {
  if (!fileList) return;
  const files = Array.from(fileList);
  const filtered = files.filter((file) => validateFile(file).isValid);
  uploadFiles(filtered);
};
var inactiveDragAreaStyle = {
  transition: "all 0.3s ease",
  border: "2px solid #fff"
};
var activeDragAreaStyle = {
  ...inactiveDragAreaStyle,
  border: "2px dashed #cccccc",
  borderColor: "#0066ff",
  backgroundColor: "#e6f7ff",
  color: "#0066ff"
};

// src/components/CandidateProfileSidebar/CandidateProfileSidebar.tsx
import { useParams } from "react-router-dom";
import { useQuery as useQuery3 } from "react-query";

// src/components/CandidateProfileSidebar/ProfileHeader.tsx
import { Button, Divider as Divider2, Flex as Flex2, Icon, Text as Text2, useModal } from "@betterleap/ui";
import { Avatar } from "@betterleap/shared";

// src/components/CandidateProfileSidebar/ContactInfoModal.tsx
import { EmailValidityIndicator, LoadingArea } from "@betterleap/shared";
import {
  Box,
  AlertIcon,
  Flex,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
  Text,
  IconButton,
  Divider,
  StyledIconButton
} from "@betterleap/ui";
import { useQuery } from "react-query";
import { Fragment as Fragment2 } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var ContactInfoListItem = ({
  text,
  onCopy,
  icon
}) => {
  return /* @__PURE__ */ jsxs(
    Flex,
    {
      justify: "between",
      css: {
        mb: 4,
        p: 8,
        borderRadius: 10,
        [`& ${StyledIconButton}`]: {
          visibility: "hidden"
        },
        hover: {
          backgroundColor: "$neutral-blue-100",
          [`& ${StyledIconButton}`]: {
            visibility: "visible"
          }
        }
      },
      children: [
        /* @__PURE__ */ jsxs(Flex, { css: { gap: 8 }, children: [
          /* @__PURE__ */ jsx2(AlertIcon, { shape: "rounded-square", variant: "gray", name: icon }),
          /* @__PURE__ */ jsx2(Text, { size: "sm", children: text })
        ] }),
        onCopy && /* @__PURE__ */ jsx2(
          IconButton,
          {
            name: "duplicate",
            label: "copy",
            variant: "ghost",
            onClick: () => onCopy(),
            css: { fill: "$neutral-blue-700" }
          }
        )
      ]
    }
  );
};
var Location = ({
  profile,
  ...rest
}) => {
  const location = profileLocation(profile);
  return /* @__PURE__ */ jsxs(Box, { ...rest, children: [
    /* @__PURE__ */ jsx2(Text, { css: { color: "$neutral-blue-700", mb: 12, fontWeight: "$medium" }, children: "Location" }),
    /* @__PURE__ */ jsx2(ContactInfoListItem, { text: location, icon: "location-marker" })
  ] });
};
var EmailAddress = ({
  emails,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs(Box, { ...rest, children: [
    /* @__PURE__ */ jsx2(Text, { css: { color: "$neutral-blue-700", mb: 12, fontWeight: "$medium" }, children: "Email Address" }),
    emails.map((email) => /* @__PURE__ */ jsx2(
      ContactInfoListItem,
      {
        text: /* @__PURE__ */ jsxs(Flex, { css: { gap: 8 }, children: [
          /* @__PURE__ */ jsx2("span", { children: email.email }),
          /* @__PURE__ */ jsx2(EmailValidityIndicator, { email })
        ] }),
        onCopy: () => navigator.clipboard.writeText(email.email),
        icon: "mail"
      },
      email.id
    ))
  ] });
};
var PhoneNumber = ({
  phones,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs(Box, { ...rest, children: [
    /* @__PURE__ */ jsx2(Text, { css: { color: "$neutral-blue-700", mb: 12, fontWeight: "$medium" }, children: "Phone Number" }),
    phones.map((phone) => /* @__PURE__ */ jsx2(
      ContactInfoListItem,
      {
        text: phone.phoneNumber,
        onCopy: () => navigator.clipboard.writeText(phone.phoneNumber),
        icon: "phone"
      },
      phone.id
    ))
  ] });
};
var ContactInfoModal = ({ profile }) => {
  const { data: contactEmailsResponse, isLoading: contactEmailsLoading } = useQuery(
    ["get_contact_emails", profile?.contactId],
    () => apiClient.contactEmail.getContactEmails({
      contactId: profile?.contactId
    }),
    {
      enabled: !!profile.contactId
    }
  );
  const { data: contactPhoneNumbersResponse, isLoading: contactPhonesLoading } = useQuery(
    ["get_contact_phone_numbers", profile?.id],
    () => apiClient.contactPhoneNumber.find({
      contactId: profile?.contactId
    }),
    {
      enabled: !!profile?.contactId
    }
  );
  const sections = [
    profileLocation(profile) ? /* @__PURE__ */ jsx2(Location, { profile }) : null,
    contactEmailsResponse?.data.length ? /* @__PURE__ */ jsx2(EmailAddress, { emails: contactEmailsResponse?.data }) : null,
    contactPhoneNumbersResponse?.data?.length ? /* @__PURE__ */ jsx2(PhoneNumber, { phones: contactPhoneNumbersResponse?.data }) : null
  ].filter(Boolean);
  return /* @__PURE__ */ jsxs(Modal, { size: "medium", children: [
    /* @__PURE__ */ jsxs(ModalHeader, { children: [
      /* @__PURE__ */ jsx2(ModalTitle, { css: { pb: 8 }, children: "Contact Info" }),
      /* @__PURE__ */ jsx2(Text, { align: "center", size: "sm", css: { color: "$gray-500" }, children: profileDisplayName(profile) })
    ] }),
    /* @__PURE__ */ jsx2(ModalBody, { css: { py: 20 }, children: /* @__PURE__ */ jsx2(LoadingArea, { isLoading: contactEmailsLoading || contactPhonesLoading, children: sections.map((section, index) => /* @__PURE__ */ jsxs(Fragment2, { children: [
      section,
      index < sections.length - 1 && /* @__PURE__ */ jsx2(Divider, { css: { my: 20 } })
    ] }, index)) }) })
  ] });
};
var ContactInfoModal_default = ContactInfoModal;

// src/components/CandidateProfileSidebar/ProfileHeader.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var ProfileHeader = ({
  candidate
}) => {
  const candidateName = constructFullName(candidate) || candidate.email || "";
  const hasSocial = candidate.linkedinUrl || candidate.githubUrl || candidate.twitterUrl || candidate.facebookUrl;
  const openContactInfoModal = useModal(ContactInfoModal_default);
  const handleContactInfoClick = () => {
    openContactInfoModal({
      profile: candidate
    });
  };
  return /* @__PURE__ */ jsxs2(Flex2, { css: { p: 16, gap: 12, borderBottom: "1px solid $neutral-blue-300" }, children: [
    /* @__PURE__ */ jsx3(
      Avatar,
      {
        size: "md",
        name: candidateName,
        image: candidate.profilePicture?.gcsUrl ?? candidate.chatUser?.imageUrl
      }
    ),
    /* @__PURE__ */ jsxs2("div", { children: [
      /* @__PURE__ */ jsx3(Text2, { css: { fontWeight: "$medium", mb: 2 }, children: candidateName }),
      /* @__PURE__ */ jsxs2(Flex2, { css: { gap: 8 }, children: [
        /* @__PURE__ */ jsx3(Button, { size: "xs", variant: "link", onClick: handleContactInfoClick, children: "Contact Info" }),
        hasSocial && /* @__PURE__ */ jsx3(
          Divider2,
          {
            orientation: "vertical",
            css: { mx: 4, backgroundColor: "$neutral-blue-300" }
          }
        ),
        candidate.linkedinUrl && /* @__PURE__ */ jsx3("a", { href: candidate.linkedinUrl, target: "_blank", children: /* @__PURE__ */ jsx3(Icon, { size: 16, name: "linked-in", css: { fill: "$blue-600" } }) }),
        candidate.githubUrl && /* @__PURE__ */ jsx3("a", { href: candidate.githubUrl, target: "_blank", children: /* @__PURE__ */ jsx3(Icon, { size: 16, name: "github", css: { fill: "$blue-600" } }) }),
        candidate.twitterUrl && /* @__PURE__ */ jsx3("a", { href: candidate.twitterUrl, target: "_blank", children: /* @__PURE__ */ jsx3(Icon, { size: 16, name: "x-logo", css: { fill: "$blue-600" } }) }),
        candidate.facebookUrl && /* @__PURE__ */ jsx3("a", { href: candidate.facebookUrl, target: "_blank", children: /* @__PURE__ */ jsx3(Icon, { size: 16, name: "facebook", css: { fill: "$blue-600" } }) })
      ] })
    ] })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateProfile.layout.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var Container = ({ children, ...props }) => /* @__PURE__ */ jsx4(
  "div",
  {
    style: {
      gridTemplateAreas: `
      "header"
      "content"
    `,
      gridTemplateColumns: "1fr",
      gridTemplateRows: "auto 1fr"
    },
    className: "h-full grid relative",
    ...props,
    children
  }
);
var Header = ({ children, ...props }) => /* @__PURE__ */ jsx4("div", { style: { gridArea: "header" }, ...props, children });
var Content = ({ children, style, ...props }) => /* @__PURE__ */ jsx4("div", { style: { gridArea: "content", overflow: "auto", ...style }, ...props, children });

// src/components/CandidateProfileSidebar/CandidateProfileSidebar.tsx
import { LoadingArea as LoadingArea3 } from "@betterleap/shared";

// src/components/CandidateProfileSidebar/EmptyState.tsx
import { Flex as Flex3, Icon as Icon2, Text as Text3 } from "@betterleap/ui";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var CandidateProfileEmptyState = ({
  icon,
  title,
  description
}) => {
  return /* @__PURE__ */ jsx5(
    Flex3,
    {
      css: {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: 24,
        textAlign: "center"
      },
      children: /* @__PURE__ */ jsxs3(Flex3, { vertical: true, css: { alignItems: "center" }, children: [
        /* @__PURE__ */ jsx5(
          Icon2,
          {
            name: icon,
            size: 40,
            css: { fill: "$neutral-blue-500", mb: 16 }
          }
        ),
        /* @__PURE__ */ jsx5(Text3, { size: "sm", css: { fontWeight: "$medium", mb: 8 }, children: title }),
        /* @__PURE__ */ jsx5(Text3, { size: "sm", css: { color: "$text-lightest" }, children: description })
      ] })
    }
  );
};

// src/components/CandidateProfileSidebar/CandidateProfileSidebar.tsx
import { Tab, Tabs, TabsList, TabContent, Box as Box6 } from "@betterleap/ui";

// src/components/CandidateProfileSidebar/CandidateFiles.tsx
import { useQuery as useQuery2 } from "react-query";
import { LoadingArea as LoadingArea2, downloadFile } from "@betterleap/shared";
import { AlertIcon as AlertIcon2, Flex as Flex4, IconButton as IconButton2, Text as Text4 } from "@betterleap/ui";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var AttachmentListItem = ({
  attachment
}) => {
  const { showGlobalError } = useChatAppContext();
  return /* @__PURE__ */ jsxs4(
    Flex4,
    {
      justify: "between",
      css: { borderBottom: "1px solid $neutral-blue-300", py: 16 },
      children: [
        /* @__PURE__ */ jsxs4(Flex4, { css: { gap: 12 }, children: [
          /* @__PURE__ */ jsx6(AlertIcon2, { name: "document-text", variant: "gray", shape: "square" }),
          /* @__PURE__ */ jsx6(Text4, { size: "sm", css: { fontWeight: "$medium" }, children: attachment.fileName })
        ] }),
        /* @__PURE__ */ jsx6(
          IconButton2,
          {
            label: "Download",
            name: "download",
            variant: "ghost",
            size: "sm",
            css: { fill: "$neutral-blue-700" },
            onClick: () => {
              downloadFile(attachment.downloadUrl, attachment.fileName, () => {
                showGlobalError("Failed to download file. Please try again.");
              });
            }
          }
        )
      ]
    }
  );
};
var CandidateFiles = ({ chatUserId }) => {
  const { data: getUserAttachmentsResult, isLoading } = useQuery2(
    ["user-attachments", { chatUserId }],
    () => apiClient.chat.getUserAttachments({
      chatUserId
    }),
    {
      enabled: !!chatUserId
    }
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx6(LoadingArea2, { css: { height: "100%" }, isLoading: true });
  }
  const attachments = getUserAttachmentsResult?.data ?? [];
  if (!attachments.length) {
    return /* @__PURE__ */ jsx6(
      CandidateProfileEmptyState,
      {
        icon: "document-text",
        title: "No files yet",
        description: "Shared files will appear here."
      }
    );
  }
  return attachments?.map((attachment) => /* @__PURE__ */ jsx6(AttachmentListItem, { attachment }, attachment.id));
};

// src/components/CandidateProfileSidebar/CandidateExperience.tsx
import { ShowMoreButton, formatDateRange } from "@betterleap/shared";
import {
  Box as Box2,
  Divider as Divider3,
  Flex as Flex5,
  Icon as Icon3,
  Text as Text5,
  useIsOverflow
} from "@betterleap/ui";
import startCase from "lodash/startCase.js";
import { Avatar as Avatar2 } from "@betterleap/shared";
import { useRef as useRef2, useState as useState3 } from "react";
import { Fragment as Fragment3, jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var formatSummary = (summary) => {
  if (!summary) return "";
  return summary.split(/\s*[\.|:|;]?\s*[\*|•|●]\s*/).filter((str) => !!str).join(". ");
};
var formatLocation = (location) => {
  if (!location) return "";
  return location.split(",").map(startCase).join(", ");
};
var ExperienceEntry = ({
  entry,
  css,
  ...rest
}) => {
  const dateRange = formatDateRange(entry);
  const overflowRef = useRef2(null);
  const [isOverflow, setIsOverflow] = useState3(false);
  const [showMore, setShowMore] = useState3(false);
  const companyAndWorkType = [
    entry.company,
    startCase((entry.employmentType || "").toLowerCase())
  ].filter((value) => !!value).join(" \u2022 ");
  const locationAndLocationType = [
    formatLocation(entry.location),
    startCase((entry.locationType || "").toLowerCase())
  ].filter((value) => !!value).join(" \u2022 ");
  useIsOverflow(
    overflowRef,
    (overflow) => {
      setIsOverflow(overflow);
    },
    {
      type: "vertical"
    }
  );
  return /* @__PURE__ */ jsxs5(Flex5, { css: { gap: 12, alignItems: "flex-start", ...css }, ...rest, children: [
    /* @__PURE__ */ jsx7(Box2, { css: { pt: 4 }, children: /* @__PURE__ */ jsx7(
      Avatar2,
      {
        name: "",
        shape: "square",
        type: "company",
        fallback: /* @__PURE__ */ jsx7(
          Icon3,
          {
            name: "briefcase",
            size: 16,
            css: { fill: "$neutral-blue-600" }
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs5(Box2, { css: { mb: 4 }, children: [
      /* @__PURE__ */ jsx7(Text5, { size: "sm", css: { fontWeight: "$medium" }, children: entry.title }),
      /* @__PURE__ */ jsx7(Text5, { size: "sm", css: { color: "$text-lightest", mb: 2 }, children: companyAndWorkType }),
      /* @__PURE__ */ jsx7(Text5, { size: "sm", css: { color: "$text-lightest", mb: 2 }, children: dateRange }),
      /* @__PURE__ */ jsx7(Text5, { size: "sm", css: { color: "$text-lightest", mb: 8 }, children: locationAndLocationType }),
      /* @__PURE__ */ jsx7(
        Text5,
        {
          ref: overflowRef,
          "data-showmore": showMore,
          size: "sm",
          css: {
            "-webkit-line-clamp": 3,
            "-webkit-box-orient": "vertical",
            display: "-webkit-box",
            lineHeight: "140%",
            color: "$text-light",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pr: 16,
            "&[data-showmore=true]": {
              overflow: "visible",
              "-webkit-line-clamp": "unset"
            }
          },
          children: formatSummary(entry.description)
        }
      ),
      (isOverflow && !showMore || showMore) && /* @__PURE__ */ jsx7(
        ShowMoreButton,
        {
          css: { fontSize: "$sm", fontWeight: "$medium" },
          showMoreText: "View more",
          showLessText: "View less",
          onToggle: () => {
            setShowMore(!showMore);
          },
          showIcon: false
        }
      )
    ] })
  ] });
};
var CandidateExperience = ({ experience }) => {
  if (!experience.length) return null;
  return /* @__PURE__ */ jsxs5(Box2, { css: { p: 16, borderBottom: "1px solid $neutral-blue-300" }, children: [
    /* @__PURE__ */ jsx7(
      Text5,
      {
        size: "sm",
        css: { color: "$text-lightest", fontWeight: "$medium", pb: 8 },
        children: "Experience"
      }
    ),
    /* @__PURE__ */ jsx7(Box2, { children: experience.map((job, index) => /* @__PURE__ */ jsxs5(Fragment3, { children: [
      /* @__PURE__ */ jsx7(
        ExperienceEntry,
        {
          css: { py: 12 },
          entry: job
        },
        `${job.title}-${index}`
      ),
      index !== experience.length - 1 && /* @__PURE__ */ jsx7(Divider3, {})
    ] })) })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateEducation.tsx
import { formatDateRange as formatDateRange2 } from "@betterleap/shared";
import {
  Box as Box3,
  Divider as Divider4,
  Flex as Flex6,
  Icon as Icon4,
  Text as Text6
} from "@betterleap/ui";
import { Avatar as Avatar3 } from "@betterleap/shared";
import { Fragment as Fragment4, jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var EducationEntry = ({
  entry,
  css,
  ...rest
}) => {
  const dateRange = formatDateRange2(entry);
  const degreeAndMajor = [entry.degree, entry.major].filter((value) => !!value).join(" \u2022 ");
  return /* @__PURE__ */ jsxs6(Flex6, { css: { gap: 12, alignItems: "flex-start", ...css }, ...rest, children: [
    /* @__PURE__ */ jsx8(Box3, { css: { pt: 4 }, children: /* @__PURE__ */ jsx8(
      Avatar3,
      {
        name: "",
        shape: "square",
        type: "company",
        fallback: /* @__PURE__ */ jsx8(
          Icon4,
          {
            name: "library",
            size: 18,
            css: { fill: "$neutral-blue-600" }
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs6(Box3, { css: { mb: 4 }, children: [
      /* @__PURE__ */ jsx8(Text6, { size: "sm", css: { fontWeight: "$medium" }, children: entry.school }),
      /* @__PURE__ */ jsx8(Text6, { size: "sm", css: { color: "$text-lightest", mb: 2 }, children: degreeAndMajor }),
      /* @__PURE__ */ jsx8(Text6, { size: "sm", css: { color: "$text-lightest", mb: 8 }, children: dateRange }),
      entry.gpa && /* @__PURE__ */ jsxs6(Text6, { size: "sm", css: { color: "$text-light" }, children: [
        "GPA: ",
        entry.gpa
      ] })
    ] })
  ] });
};
var CandidateEducation = ({ education }) => {
  if (!education.length) return null;
  return /* @__PURE__ */ jsxs6(Box3, { css: { p: 16, borderBottom: "1px solid $neutral-blue-300" }, children: [
    /* @__PURE__ */ jsx8(
      Text6,
      {
        size: "sm",
        css: { color: "$text-lightest", fontWeight: "$medium", py: 8 },
        children: "Education"
      }
    ),
    /* @__PURE__ */ jsx8(Box3, { children: education.map((school, index) => /* @__PURE__ */ jsxs6(Fragment4, { children: [
      /* @__PURE__ */ jsx8(EducationEntry, { css: { py: 12 }, entry: school }, school.id),
      index !== education.length - 1 && /* @__PURE__ */ jsx8(Divider4, {})
    ] })) })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateCertifications.tsx
import { formatDateRange as formatDateRange3 } from "@betterleap/shared";
import {
  Box as Box4,
  Divider as Divider5,
  Flex as Flex7,
  Icon as Icon5,
  Text as Text7
} from "@betterleap/ui";
import { Avatar as Avatar4 } from "@betterleap/shared";
import { Fragment as Fragment5, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var CredentialEntry = ({
  entry,
  css,
  ...rest
}) => {
  const dateRange = formatDateRange3(entry);
  return /* @__PURE__ */ jsxs7(Flex7, { css: { gap: 12, alignItems: "flex-start", ...css }, ...rest, children: [
    /* @__PURE__ */ jsx9(Box4, { css: { pt: 4 }, children: /* @__PURE__ */ jsx9(
      Avatar4,
      {
        name: "",
        shape: "square",
        type: "company",
        fallback: /* @__PURE__ */ jsx9(
          Icon5,
          {
            name: "academic-cap",
            size: 18,
            css: { fill: "$neutral-blue-600" }
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs7(Box4, { css: { mb: 4 }, children: [
      /* @__PURE__ */ jsx9(Text7, { size: "sm", css: { fontWeight: "$medium" }, children: entry.name }),
      /* @__PURE__ */ jsx9(Text7, { size: "sm", css: { color: "$text-lightest", mb: 2 }, children: entry.organization }),
      /* @__PURE__ */ jsx9(Text7, { size: "sm", css: { color: "$text-lightest", mb: 8 }, children: dateRange })
    ] })
  ] });
};
var CandidateCredentials = ({
  credentials
}) => {
  if (!credentials.length) return null;
  return /* @__PURE__ */ jsxs7(Box4, { css: { p: 16, borderBottom: "1px solid $neutral-blue-300" }, children: [
    /* @__PURE__ */ jsx9(
      Text7,
      {
        size: "sm",
        css: { color: "$text-lightest", fontWeight: "$medium", py: 8 },
        children: "Credentials"
      }
    ),
    /* @__PURE__ */ jsx9(Box4, { children: credentials.map((credential, index) => /* @__PURE__ */ jsxs7(Fragment5, { children: [
      /* @__PURE__ */ jsx9(
        CredentialEntry,
        {
          css: { py: 12 },
          entry: credential
        },
        credential.id
      ),
      index !== credentials.length - 1 && /* @__PURE__ */ jsx9(Divider5, {})
    ] })) })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateSkills.tsx
import { Box as Box5, Flex as Flex8, Icon as Icon6, Text as Text8 } from "@betterleap/ui";
import { Avatar as Avatar5 } from "@betterleap/shared";
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var CandidateSkills = ({ skills }) => {
  if (!skills.length) return null;
  return /* @__PURE__ */ jsxs8(Box5, { css: { p: 16 }, children: [
    /* @__PURE__ */ jsx10(
      Text8,
      {
        size: "sm",
        css: { color: "$text-lightest", fontWeight: "$medium", py: 8 },
        children: "Skills"
      }
    ),
    /* @__PURE__ */ jsxs8(Flex8, { css: { gap: 12, alignItems: "flex-start" }, children: [
      /* @__PURE__ */ jsx10(Box5, { css: { pt: 4 }, children: /* @__PURE__ */ jsx10(
        Avatar5,
        {
          name: "",
          shape: "square",
          type: "company",
          fallback: /* @__PURE__ */ jsx10(
            Icon6,
            {
              name: "lightning-bolt",
              size: 18,
              css: { fill: "$neutral-blue-600" }
            }
          )
        }
      ) }),
      /* @__PURE__ */ jsx10(Text8, { size: "sm", css: { color: "$text-lightest" }, children: skills.filter((skill) => !!skill.name).map((skill) => skill.name).join(" \u2022 ") })
    ] })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateDetails.tsx
import { Fragment as Fragment6, jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var CandidateDetails = ({ candidate }) => {
  const hasDetails = !!candidate?.experience?.length || !!candidate?.education?.length || !!candidate?.credentials?.length || !!candidate?.skills?.length;
  if (!hasDetails) {
    return /* @__PURE__ */ jsx11(
      CandidateProfileEmptyState,
      {
        icon: "identification",
        title: "No candidate details yet",
        description: "Once the candidate updates the profile, you'll see their experience and details here."
      }
    );
  }
  return /* @__PURE__ */ jsxs9(Fragment6, { children: [
    /* @__PURE__ */ jsx11(CandidateExperience, { experience: candidate?.experience ?? [] }),
    /* @__PURE__ */ jsx11(CandidateEducation, { education: candidate?.education ?? [] }),
    /* @__PURE__ */ jsx11(CandidateCredentials, { credentials: candidate?.credentials ?? [] }),
    /* @__PURE__ */ jsx11(CandidateSkills, { skills: candidate?.skills ?? [] })
  ] });
};

// src/components/CandidateProfileSidebar/CandidateProfileSidebar.tsx
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var CandidateProfileSidebar = () => {
  const { state } = useChatAppContext();
  const { chatId } = useParams();
  const chat = chatSelector(state, chatId);
  const { data: profilePreviewResult, isLoading } = useQuery3(
    ["profilePreview", { chatUserId: chat?.subjectUser?.id }],
    () => apiClient.candidateProfile.getProfilePreview({
      chatUserId: chat?.subjectUser?.id
    }),
    {
      enabled: !!chat?.subjectUser?.id
    }
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx12(LoadingArea3, { css: { height: "100%" }, isLoading: true });
  }
  const candidate = profilePreviewResult?.data;
  if (!candidate) {
    return /* @__PURE__ */ jsx12(
      CandidateProfileEmptyState,
      {
        icon: "identification",
        title: "No candidate details yet",
        description: "Once the candidate updates the profile, you\u2019ll see their experience and details here."
      }
    );
  }
  return /* @__PURE__ */ jsxs10(Container, { children: [
    /* @__PURE__ */ jsx12(Header, { children: /* @__PURE__ */ jsx12(ProfileHeader, { candidate }) }),
    /* @__PURE__ */ jsx12(Content, { children: /* @__PURE__ */ jsxs10(
      Tabs,
      {
        css: { height: "100%", pt: 16 },
        tabStyle: "pill",
        defaultValue: "details",
        children: [
          /* @__PURE__ */ jsx12(Box6, { css: { px: 16, pb: 16 }, children: /* @__PURE__ */ jsxs10(TabsList, { css: { minHeight: 38 }, children: [
            /* @__PURE__ */ jsx12(Tab, { value: "details", children: "Details" }),
            /* @__PURE__ */ jsx12(Tab, { value: "files", children: "Files" })
          ] }) }),
          /* @__PURE__ */ jsx12(TabContent, { value: "details", children: /* @__PURE__ */ jsx12(CandidateDetails, { candidate }) }),
          /* @__PURE__ */ jsx12(TabContent, { value: "files", css: { px: 16 }, children: /* @__PURE__ */ jsx12(CandidateFiles, { chatUserId: chat?.subjectUser?.id }) })
        ]
      }
    ) })
  ] });
};

// src/components/ChatWindowWrapper.tsx
import { Flex as Flex11, showToast as showToast3, Spinner as Spinner2 } from "@betterleap/ui";
import { useEffect as useEffect8, useState as useState8 } from "react";

// src/context/test-helpers.ts
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}

// src/hooks/useChatMessagePagination.ts
import { showToast as showToast2 } from "@betterleap/ui";
var useChatMessagePagination = (chatId) => {
  const appCtx = useChatAppContext();
  const { state, dispatch } = appCtx;
  const chatState = chatId ? state.chats[chatId] : null;
  const chat = chatState?.chat;
  const oldestLoadedMessage = chatState?.messages?.[0];
  const newestLoadedMessage = chatState?.messages?.[chatState.messages.length - 1];
  const isLoadingOlder = chatState?.isLoadingOlder;
  const isLoadingNewer = chatState?.isLoadingNewer;
  const hasOlderMessages = oldestLoadedMessage && chatState.oldestMessageDate ? new Date(oldestLoadedMessage.createdAt).getTime() > new Date(chatState.oldestMessageDate).getTime() : null;
  const hasNewerMessages = newestLoadedMessage && chatState.newestMessageDate ? new Date(newestLoadedMessage.createdAt).getTime() < new Date(chatState.newestMessageDate).getTime() : null;
  const fetchOlderMessages = async ({
    limit = 50
  } = {}) => {
    if (!chat || chat.isPreChat) return;
    dispatch({
      type: "SET_CHAT_LOADING" /* SET_CHAT_LOADING */,
      payload: {
        chatId,
        isLoadingOlder: true
      }
    });
    try {
      const { data } = await apiClient.chat.paginatedChatMessages({
        chatId: chat.id,
        before: oldestLoadedMessage?.createdAt || (/* @__PURE__ */ new Date()).toISOString(),
        limit
      });
      if (data) {
        dispatch({
          type: "UPDATE_CHAT_MESSAGES" /* UPDATE_CHAT_MESSAGES */,
          payload: {
            workspaceId: chat.workspaceId,
            chatId: chat.id,
            data
          }
        });
      }
    } catch (err) {
      console.log("fetchOlderMessages error", err);
      const error = err;
      showToast2({
        title: "Couldn't fetch messages",
        description: error.message,
        variant: "danger"
      });
    } finally {
      dispatch({
        type: "SET_CHAT_LOADING" /* SET_CHAT_LOADING */,
        payload: {
          chatId,
          isLoadingOlder: false
        }
      });
    }
  };
  const fetchNewerMessages = async ({
    limit = 50
  } = {}) => {
    if (!chat || chat.isPreChat) return;
    dispatch({
      type: "SET_CHAT_LOADING" /* SET_CHAT_LOADING */,
      payload: {
        chatId,
        isLoadingNewer: true
      }
    });
    try {
      const { data } = await apiClient.chat.paginatedChatMessages({
        chatId: chat.id,
        after: newestLoadedMessage ? newestLoadedMessage.createdAt : void 0,
        limit
      });
      if (data) {
        dispatch({
          type: "UPDATE_CHAT_MESSAGES" /* UPDATE_CHAT_MESSAGES */,
          payload: {
            workspaceId: chat.workspaceId,
            chatId: chat.id,
            data
          }
        });
      }
    } catch (err) {
      console.log("fetchNewerMessages error", err);
      const error = err;
      showToast2({
        title: "Couldn't fetch messages",
        description: error.message,
        variant: "danger"
      });
    } finally {
      dispatch({
        type: "SET_CHAT_LOADING" /* SET_CHAT_LOADING */,
        payload: {
          chatId,
          isLoadingNewer: false
        }
      });
    }
  };
  const res = {
    fetchOlderMessages,
    fetchNewerMessages,
    isLoadingOlder,
    isLoadingNewer,
    hasOlderMessages,
    hasNewerMessages
  };
  return res;
};

// src/components/ChatWindow.tsx
import { Spinner } from "@betterleap/ui";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useMemo, useRef as useRef9 } from "react";
import { Button as Button2, FileTrigger } from "react-aria-components";

// src/hooks/useChatUpload.ts
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable
} from "firebase/storage";
import { useRef as useRef3, useState as useState4 } from "react";
function randomishString(timestamp) {
  const random = Math.floor(Math.random() * 1e4).toString(16);
  return `c${timestamp.toString(16)}_${random}`;
}
function mapsToUploadStates(progressMap, resultsMap, errorsMap) {
  return Array.from(progressMap.entries()).map(([file, progress]) => ({
    file,
    progress,
    result: resultsMap.get(file),
    error: errorsMap.get(file)
  }));
}
function useChatUpload() {
  const progressMap = useRef3(/* @__PURE__ */ new Map());
  const resultsMap = useRef3(/* @__PURE__ */ new Map());
  const errorsMap = useRef3(/* @__PURE__ */ new Map());
  const [uploadState, setUploadState] = useState4([]);
  const uploadFiles = async (files) => {
    const storage = getStorage();
    const promises = files.map((file) => {
      const storageRef = ref(
        storage,
        `chat-attachments/${randomishString(Date.now())}/${file.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, file);
      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
            progressMap.current.set(file, progress);
            const nextState = mapsToUploadStates(
              progressMap.current,
              resultsMap.current,
              errorsMap.current
            );
            setUploadState(nextState);
          },
          (err) => {
            errorsMap.current.set(file, err);
            reject(err);
          },
          async () => {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            const { data: attachment } = await apiClient.chat.saveAttachment({
              requestBody: {
                gcsUrl: uploadTask.snapshot.ref.toString(),
                downloadUrl,
                fileName: file.name,
                fileSizeBytes: file.size.toString(),
                mimeType: file.type
              }
            });
            resultsMap.current.set(file, attachment);
            const nextState = mapsToUploadStates(
              progressMap.current,
              resultsMap.current,
              errorsMap.current
            );
            setUploadState(nextState);
            resolve(attachment);
          }
        );
      });
    });
    await Promise.allSettled(promises);
    return files.map((file) => {
      const progress = progressMap.current.get(file) ?? 100;
      return {
        file,
        progress
      };
    });
  };
  const removeUpload = (ref2) => {
    let file;
    if (ref2 instanceof File) {
      file = ref2;
    } else if ("file" in ref2) {
      file = ref2.file;
    }
    if (file) {
      progressMap.current.delete(file);
      resultsMap.current.delete(file);
      errorsMap.current.delete(file);
      setUploadState((prev) => prev.filter((state) => state.file !== file));
    }
  };
  const clearAll = () => {
    progressMap.current.clear();
    resultsMap.current.clear();
    errorsMap.current.clear();
    setUploadState([]);
  };
  return [uploadState, uploadFiles, removeUpload, clearAll];
}

// src/hooks/useDragOverStyle.ts
import { useState as useState5, useCallback as useCallback2 } from "react";
function useDragOverStyle(defaultStyle, dragOverStyle) {
  const [style, setStyle] = useState5(defaultStyle);
  const [dragActive, setDragActive] = useState5(false);
  const handleDragOver = useCallback2(
    (event) => {
      event.preventDefault();
      setStyle(dragOverStyle);
      setDragActive(true);
    },
    [dragOverStyle]
  );
  const handleDragLeave = useCallback2(
    (_event) => {
      setStyle(defaultStyle);
      setDragActive(false);
    },
    [defaultStyle]
  );
  const handleDrop = useCallback2(
    (event) => {
      event.preventDefault();
      setStyle(defaultStyle);
      setDragActive(false);
    },
    [defaultStyle]
  );
  return {
    style,
    dragActive,
    handleDragOver,
    handleDragLeave,
    handleDrop
  };
}

// src/hooks/useScrollAnchor.ts
import { useRef as useRef4, useLayoutEffect } from "react";
function useScrollAnchor(dependencies = []) {
  const scrollRef = useRef4(null);
  const prevScrollHeightRef = useRef4(0);
  useLayoutEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const newScrollHeight = scrollContainer.scrollHeight;
    const oldScrollHeight = prevScrollHeightRef.current;
    const scrollDifference = newScrollHeight - oldScrollHeight;
    if (scrollDifference > 0) {
      scrollContainer.scrollTop += scrollDifference;
    }
    prevScrollHeightRef.current = newScrollHeight;
  }, dependencies);
  return scrollRef;
}
var useScrollAnchor_default = useScrollAnchor;

// src/icons/PaperClip.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var PaperClipIcon = (props) => /* @__PURE__ */ jsx13(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 20,
    height: 20,
    fill: "none",
    viewBox: "0 0 20 20",
    ...props,
    children: /* @__PURE__ */ jsx13(
      "path",
      {
        fill: "#656F7D",
        fillRule: "evenodd",
        d: "M10.588 3.915 5.24 9.406a4.375 4.375 0 1 0 6.187 6.188l5.215-5.203a.625.625 0 1 1 .883.885l-5.214 5.201a5.625 5.625 0 0 1-7.958-7.951L9.7 3.034a3.958 3.958 0 0 1 5.601 5.595l-5.348 5.492a2.292 2.292 0 0 1-3.241-3.241L12.2 5.39a.625.625 0 0 1 .884.884l-5.488 5.488a1.042 1.042 0 0 0 1.47 1.476l5.348-5.49a2.708 2.708 0 0 0-3.827-3.834",
        clipRule: "evenodd"
      }
    )
  }
);

// src/components/Attachments.tsx
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";

// src/icons/Close.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var CloseIcon = (props) => /* @__PURE__ */ jsx14(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx14(
      "path",
      {
        fill: "#656F7D",
        fillRule: "evenodd",
        d: "M.558.558a.625.625 0 0 1 .884 0L6 5.116 10.558.558a.625.625 0 1 1 .884.884L6.884 6l4.558 4.558a.625.625 0 1 1-.884.884L6 6.884l-4.558 4.558a.625.625 0 1 1-.884-.884L5.116 6 .558 1.442a.625.625 0 0 1 0-.884",
        clipRule: "evenodd"
      }
    )
  }
);

// src/icons/Document.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var DocumentIcon = (props) => /* @__PURE__ */ jsx15(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 16,
    viewBox: "0 0 12 16",
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx15(
      "path",
      {
        fill: "#656F7D",
        fillRule: "evenodd",
        d: "M2.25 1.813a.937.937 0 0 0-.937.937v10.5c0 .518.42.938.937.938h7.5c.518 0 .938-.42.938-.938V6.06c0-.05-.02-.097-.055-.132l-4.061-4.06a.19.19 0 0 0-.133-.056zM.188 2.75C.188 1.61 1.11.688 2.25.688h4.19c.347 0 .681.138.927.384l4.061 4.06c.246.247.384.58.384.929v7.189c0 1.14-.923 2.063-2.062 2.063h-7.5A2.06 2.06 0 0 1 .188 13.25zm3 5.25c0-.31.251-.562.562-.562h4.5a.563.563 0 0 1 0 1.125h-4.5A.563.563 0 0 1 3.188 8m0 3c0-.31.251-.562.562-.562h4.5a.562.562 0 1 1 0 1.124h-4.5A.56.56 0 0 1 3.188 11",
        clipRule: "evenodd"
      }
    )
  }
);

// src/icons/Download.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var DownloadIcon = (props) => /* @__PURE__ */ jsx16(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 20,
    height: 20,
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx16(
      "path",
      {
        fill: "#656F7D",
        fillRule: "evenodd",
        d: "M3.958 14.167c0 1.035.84 1.875 1.875 1.875h8.334c1.035 0 1.875-.84 1.875-1.875v-.834a.625.625 0 1 1 1.25 0v.834c0 1.726-1.4 3.125-3.125 3.125H5.833a3.125 3.125 0 0 1-3.125-3.125v-.834a.625.625 0 1 1 1.25 0zm2.267-4.609a.625.625 0 0 1 .884 0l2.266 2.267V3.332a.625.625 0 1 1 1.25 0v8.492l2.266-2.267a.625.625 0 0 1 .884.884l-3.333 3.333a.625.625 0 0 1-.884 0l-3.333-3.333a.625.625 0 0 1 0-.884",
        clipRule: "evenodd"
      }
    )
  }
);

// src/components/Attachments.tsx
import { downloadFile as downloadFile2 } from "@betterleap/shared";
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
var Attachments = ({ files, removeUpload, className }) => {
  if (!files?.length) {
    return null;
  }
  return /* @__PURE__ */ jsx17("div", { className: `flex gap-4 ${className}`, children: files.map((file) => {
    const key = "id" in file ? file.id : file.file.name;
    return /* @__PURE__ */ jsx17(FileItem, { file, onDelete: removeUpload }, key);
  }) });
};
var FileItem = ({ file, onDelete }) => {
  if ("downloadUrl" in file) {
    return /* @__PURE__ */ jsxs11(
      "button",
      {
        onClick: () => {
          downloadFile2(file.downloadUrl, file.fileName);
        },
        className: `flex relative items-center text-black justify-between p-1.5 text-sm font-medium bg-white border-[#E8EAED] border rounded-[12px] overflow-hidden hover:border-blue-500 transition-all ${!file && "opacity-50"}`,
        children: [
          /* @__PURE__ */ jsx17(DocumentIcon, { className: "text-gray-500 bg-[#F7F8F9] p-1 rounded-[6px] w-7 h-7" }),
          /* @__PURE__ */ jsx17("span", { className: "ml-2", children: file.fileName }),
          /* @__PURE__ */ jsx17("div", { className: "ml-2", children: /* @__PURE__ */ jsx17(DownloadIcon, { className: "w-5 h-5 text-gray-500" }) })
        ]
      },
      file.id
    );
  }
  const { file: fileData, result, error, progress } = file;
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      className: `flex relative items-center text-black justify-between p-1.5 text-sm font-medium bg-white border-[#E8EAED] border rounded-[12px] overflow-hidden ${!result && "opacity-50"}`,
      children: [
        /* @__PURE__ */ jsx17(DocumentIcon, { className: "text-gray-500 bg-[#F7F8F9] p-1 rounded-[6px] w-7 h-7" }),
        /* @__PURE__ */ jsx17("span", { className: "ml-2", children: fileData.name }),
        error && /* @__PURE__ */ jsxs11("div", { className: "flex items-center ml-4 text-red-600", children: [
          /* @__PURE__ */ jsx17(ExclamationCircleIcon, { className: "w-5 h-5" }),
          /* @__PURE__ */ jsx17("span", { className: "ml-2", children: error.message })
        ] }),
        onDelete && /* @__PURE__ */ jsx17("button", { onClick: () => onDelete(file), className: "bg-white pl-2 mr-2", children: /* @__PURE__ */ jsx17(CloseIcon, { className: "w-3 h-3 text-gray-500 -mb-[2px]" }) }),
        !result && /* @__PURE__ */ jsx17(
          "span",
          {
            style: { width: `${progress}%` },
            className: `z-0 opacity-10 text-blue-600 bg-blue-500 absolute top-0 bottom-0 left-0 transition-all ease-in-out duration-200`
          }
        )
      ]
    },
    fileData.name
  );
};

// src/components/AutoGrowTextArea.tsx
import { useEffect as useEffect4, useRef as useRef5 } from "react";
import { jsx as jsx18 } from "react/jsx-runtime";
function AutoGrowTextArea({
  className,
  autoFocus,
  style,
  value,
  ...props
}) {
  const ref2 = useRef5(null);
  const resize = () => {
    const textarea = ref2.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };
  useEffect4(() => {
    const textarea = ref2.current;
    if (!textarea) return;
    textarea.addEventListener("input", resize);
    return () => {
      textarea.removeEventListener("input", resize);
    };
  }, []);
  useEffect4(() => {
    const textarea = ref2.current;
    if (!textarea || !autoFocus) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [autoFocus]);
  useEffect4(() => {
    if (ref2.current && value === "") {
      ref2.current.style.height = "auto";
    }
  }, [value]);
  return /* @__PURE__ */ jsx18(
    "textarea",
    {
      className: className + " resize-none",
      rows: 1,
      ref: ref2,
      style,
      value,
      ...props
    }
  );
}

// src/components/ChatWindow.tsx
import { Avatar as Avatar7 } from "@betterleap/shared";

// src/components/ChatColumn.layout.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var Container2 = ({
  showHeader = true,
  children,
  ...props
}) => {
  const gridTemplateAreas = ['"list"', '"footer"'];
  const gridTemplateRows = ["1fr", "minmax(81px, auto)"];
  if (showHeader) {
    gridTemplateAreas.unshift('"header"');
    gridTemplateRows.unshift("minmax(60px, auto)");
  }
  return /* @__PURE__ */ jsx19(
    "div",
    {
      style: {
        gridTemplateAreas: `${gridTemplateAreas.join(" ")}`,
        gridTemplateColumns: "1fr",
        gridTemplateRows: gridTemplateRows.join(" ")
      },
      className: "h-full grid relative",
      ...props,
      children
    }
  );
};
var Header2 = ({ children, ...props }) => /* @__PURE__ */ jsx19("div", { style: { gridArea: "header" }, ...props, children });
var List = ({ children, style, ...props }) => /* @__PURE__ */ jsx19("div", { style: { gridArea: "list", overflow: "auto", ...style }, ...props, children });
var Footer = ({ children, ...props }) => /* @__PURE__ */ jsx19("div", { style: { gridArea: "footer" }, ...props, children });

// src/components/ChatMessages.tsx
import { motion as motion2 } from "framer-motion";
import { Avatar as Avatar6 } from "@betterleap/shared";

// src/components/ChatMessageBubble.tsx
import { cva } from "cva";
import { motion } from "framer-motion";
import { useRef as useRef7, useState as useState6 } from "react";

// src/hooks/useIntersectionObserver.ts
import { useEffect as useEffect5, useRef as useRef6 } from "react";
function useIntersectionObserver(callback) {
  const ref2 = useRef6(null);
  const cbRef = useRef6(callback);
  useEffect5(() => {
    cbRef.current = callback;
  }, [callback]);
  useEffect5(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          cbRef.current(entry.isIntersecting);
        }
      },
      { threshold: 0.5 }
    );
    const element = ref2.current;
    if (!element) return;
    observer.observe(element);
    return () => {
      observer.unobserve(element);
    };
  }, []);
  return ref2;
}

// src/components/ChatMessageContent.tsx
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { jsx as jsx20 } from "react/jsx-runtime";
var ChatMessageContent = ({ content }) => {
  return /* @__PURE__ */ jsx20(
    ReactMarkdown,
    {
      remarkPlugins: [remarkGfm],
      linkTarget: "_blank",
      allowedElements: ["b", "strong", "a", "i", "em", "p", "span"],
      components: {
        b: ({ children }) => /* @__PURE__ */ jsx20("strong", { className: "font-semibold", children }),
        strong: ({ children }) => /* @__PURE__ */ jsx20("strong", { className: "font-semibold", children }),
        a: ({ children, href }) => /* @__PURE__ */ jsx20(
          "a",
          {
            href,
            target: "_blank",
            rel: "noreferrer",
            className: "underline",
            children
          }
        ),
        i: ({ children }) => /* @__PURE__ */ jsx20("em", { className: "italic", children }),
        em: ({ children }) => /* @__PURE__ */ jsx20("em", { className: "italic", children })
      },
      children: content
    }
  );
};

// src/components/ChatMessageBubble.tsx
import { jsx as jsx21, jsxs as jsxs12 } from "react/jsx-runtime";
var chatMessageOuter = cva({
  base: "flex flex-col"
});
var chatMessageBubble = cva({
  base: "whitespace-pre-wrap text-[#1A1C20] leading-6",
  variants: {
    isSending: {
      true: "bg-opacity-50"
    }
  },
  defaultVariants: {
    isSending: false
  }
});
function ChatMessageBubble({ message, user, onRead }) {
  const timeoutRef = useRef7();
  const isCurrentUser = message.senderId === user.id;
  const isSending = isCurrentUser && message.status === "sending" /* Sending */;
  const readByMe = isCurrentUser || message.userState?.some(
    (state) => state.userId === user.id && !!state.readAt
  );
  const attachments = message.attachments;
  const [inView, setInView] = useState6(readByMe);
  const ref2 = useIntersectionObserver(() => {
    if (!inView) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        setInView(true);
        onRead(message.id);
      }, 2e3);
    }
  });
  return /* @__PURE__ */ jsxs12(
    motion.div,
    {
      ref: ref2,
      initial: isSending ? { y: isCurrentUser ? 10 : 0 } : false,
      animate: { y: 0 },
      className: chatMessageOuter(),
      "data-id": message.id,
      children: [
        /* @__PURE__ */ jsx21(
          "div",
          {
            className: chatMessageBubble({
              isSending
            }),
            children: /* @__PURE__ */ jsx21(ChatMessageContent, { content: message.content })
          }
        ),
        !!attachments?.length && /* @__PURE__ */ jsx21("div", { className: "py-4", children: /* @__PURE__ */ jsx21(Attachments, { files: attachments }) })
      ]
    }
  );
}

// src/components/ChatMessages.tsx
import { format as format2 } from "date-fns";

// src/components/UnreadMessageBreak.tsx
import { Divider as Divider6, Flex as Flex9, Text as Text9 } from "@betterleap/ui";
import { jsx as jsx22, jsxs as jsxs13 } from "react/jsx-runtime";
var pluralize = (count, singular, plural) => count === 1 ? singular : plural;
var UnreadMessageBreak = ({
  unreadCount,
  css,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs13(Flex9, { css: { gap: 12, ...css }, ...rest, children: [
    /* @__PURE__ */ jsxs13(Text9, { size: "xs", noBreak: true, css: { color: "$purple-600" }, children: [
      unreadCount,
      " new ",
      pluralize(unreadCount, "message", "messages")
    ] }),
    /* @__PURE__ */ jsx22(Divider6, { css: { backgroundColor: "$purple-600" } })
  ] });
};

// src/components/ChatMessages.tsx
import React2 from "react";
import { jsx as jsx23, jsxs as jsxs14 } from "react/jsx-runtime";
function ChatMessages({
  messages,
  user,
  workspace,
  onRead,
  unreadId,
  unreadCount
}) {
  const firstMessage = messages[0];
  const lastMessage = messages[messages.length - 1];
  const sender = firstMessage?.sender;
  const senderName = sender?.role === "recruiter" ? workspace?.name ?? "" : profileDisplayName(firstMessage.sender?.profile);
  const isSending = firstMessage.status === "sending" /* Sending */;
  const avatar = getSenderAvatar({ workspace, sender });
  return /* @__PURE__ */ jsx23(motion2.div, { initial: isSending ? { y: 10 } : false, animate: { y: 0 }, children: /* @__PURE__ */ jsxs14("div", { className: "mb-6", children: [
    /* @__PURE__ */ jsxs14("div", { className: "flex gap-2 items-center mb-0.5", children: [
      /* @__PURE__ */ jsx23(Avatar6, { name: senderName, image: avatar }),
      /* @__PURE__ */ jsx23("p", { className: "text-sm font-medium text-[#1A1C20]", children: senderName }),
      /* @__PURE__ */ jsx23("span", { className: "text-[8px] text-[#656F7D]", children: "\u2022" }),
      /* @__PURE__ */ jsx23("p", { className: "text-xs text-[#656F7D]", children: format2(new Date(lastMessage.createdAt), "h:mm a") })
    ] }),
    /* @__PURE__ */ jsxs14("div", { className: "ml-10", children: [
      sender?.role === "recruiter" && /* @__PURE__ */ jsx23("p", { className: "text-sm text-[#656F7D] mb-2", children: profileDisplayName(firstMessage.sender?.profile) }),
      messages.map((message, i) => /* @__PURE__ */ jsxs14(React2.Fragment, {
        // the unread message break is always shown above the avatar if it's the first message
        children: [
          i !== 0 && message.id === unreadId && /* @__PURE__ */ jsx23(
            UnreadMessageBreak,
            {
              css: { my: 24, ml: -40 },
              unreadCount
            }
          ),
          /* @__PURE__ */ jsx23(
            ChatMessageBubble,
            {
              message,
              user,
              onRead
            },
            message.id
          )
        ]
      }, message.id))
    ] })
  ] }) });
}

// src/components/MessageDateBreak.tsx
import { Box as Box7, Divider as Divider7, Flex as Flex10, Text as Text10 } from "@betterleap/ui";
import { jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
var MessageDateBreak = ({
  dateString,
  css,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs15(Flex10, { css: { gap: 12, ...css }, ...rest, children: [
    /* @__PURE__ */ jsx24(Divider7, { css: { backgroundColor: "$neutral-blue-300" } }),
    /* @__PURE__ */ jsx24(
      Box7,
      {
        css: {
          borderRadius: 6,
          px: 6,
          py: 2,
          backgroundColor: "$neutral-blue-300"
        },
        children: /* @__PURE__ */ jsx24(Text10, { size: "xs", noBreak: true, css: { color: "$neutral-blue-700" }, children: dateString })
      }
    ),
    /* @__PURE__ */ jsx24(Divider7, { css: { backgroundColor: "$neutral-blue-300" } })
  ] });
};

// src/components/Sentinal.tsx
import { useEffect as useEffect6, useRef as useRef8 } from "react";
import { jsx as jsx25 } from "react/jsx-runtime";
function Sentinal({ onInViewport }) {
  const sentinalRef = useRef8(null);
  useEffect6(() => {
    if (!onInViewport) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onInViewport();
        }
      },
      { threshold: 0.1 }
    );
    if (sentinalRef.current) {
      observer.observe(sentinalRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [onInViewport]);
  return /* @__PURE__ */ jsx25("div", { "data-sentinal": true, ref: sentinalRef });
}

// src/components/TypingStatus.tsx
import { useState as useState7, useEffect as useEffect7 } from "react";
import { jsx as jsx26 } from "react/jsx-runtime";
var TypingStatusLabel = ({
  statusInfo
}) => {
  const [label, setLabel] = useState7(null);
  useEffect7(() => {
    if (!statusInfo) return;
    const { userName, isTyping } = statusInfo;
    setLabel(isTyping ? `${userName} is typing...` : "");
    setTimeout(() => {
      setLabel("");
    }, 2e3);
  }, [statusInfo]);
  return /* @__PURE__ */ jsx26("div", { className: "text-gray-500 text-sm pl-2 pb-2 absolute bottom-[73px] italic", children: label });
};

// src/components/ChatWindow.tsx
import { jsx as jsx27, jsxs as jsxs16 } from "react/jsx-runtime";
function ChatWindow({
  user,
  chat,
  messages,
  onChange,
  onSubmit,
  showHeader = true,
  showBackButton,
  onClickBack,
  unreadCount,
  hasMessagesLoaded,
  emptyState,
  markMessageAsRead,
  setIsTyping,
  isLoadingOlder,
  isLoadingNewer,
  hasOlderMessages,
  hasNewerMessages,
  onFetchOlderMessages,
  onFetchNewerMessages,
  showSendingSpinner
}) {
  const { state, dispatch } = useChatAppContext();
  const { typingStatus } = chat;
  const formRef = useRef9(null);
  const [uploadState, uploadFiles, removeUpload, clearAll] = useChatUpload();
  const combinedMessages = messages?.concat(chat.inflightMessages || []).sort((a, b) => {
    const aCreatedAt = a.createdAt;
    const bCreatedAt = b.createdAt;
    if (aCreatedAt > bCreatedAt) return 1;
    if (aCreatedAt < bCreatedAt) return -1;
    return 0;
  }) || [];
  const groupedMessages = Object.entries(
    groupByDateAndAuthor(combinedMessages)
  );
  const { firstUnreadId } = useMemo(
    () => getMessageBreaks(combinedMessages, user.id),
    [combinedMessages.length]
  );
  const pinnedUnreadCount = useMemo(
    () => unreadCount,
    [combinedMessages.length]
  );
  const chatWorkspace = findWorkspaceById(state, chat.workspaceId);
  const { style, dragActive, handleDragOver, handleDragLeave, handleDrop } = useDragOverStyle(inactiveDragAreaStyle, activeDragAreaStyle);
  const avatar = getWorkspaceAvatar({
    workspace: chatWorkspace,
    user,
    subject: chat.subjectUser
  });
  let chatSubject = chat.subject || "Unknown";
  if (user.role === "candidate" && chatWorkspace) {
    chatSubject = chatWorkspace.name;
  }
  const chatListRef = useScrollAnchor_default([combinedMessages.length]);
  async function readMessage(messageId) {
    const payload = await markMessageAsRead(messageId, chat.id);
    dispatch({
      type: "READ_MESSAGE" /* READ_MESSAGE */,
      payload
    });
  }
  function sendMessage() {
    setIsTyping(false, chat.id, Date.now());
    formRef.current?.dispatchEvent(
      new Event("submit", { bubbles: true, cancelable: true })
    );
  }
  function onInputChange(e) {
    onChange(e);
    setIsTyping(!!e.target.value, chat.id, Date.now());
  }
  const scrollToBottom = () => {
    if (chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
    }
  };
  return /* @__PURE__ */ jsxs16(Container2, { showHeader, children: [
    showHeader && /* @__PURE__ */ jsxs16(Header2, { className: "font-medium p-4 border-b flex items-center gap-2", children: [
      showBackButton && /* @__PURE__ */ jsx27("button", { className: "text-purple-500 mr-2 px-2", onClick: onClickBack, children: /* @__PURE__ */ jsx27(ChevronLeftIcon, { className: "w-5 h-5" }) }),
      /* @__PURE__ */ jsx27(Avatar7, { image: avatar, name: chatSubject }),
      /* @__PURE__ */ jsx27("span", { children: chatSubject })
    ] }),
    /* @__PURE__ */ jsxs16(
      List,
      {
        className: "relative",
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        style,
        onDrop: (event) => {
          event.preventDefault();
          handleDrop(event);
          checkAndUploadFiles(event.dataTransfer.files, uploadFiles);
        },
        children: [
          dragActive && /* @__PURE__ */ jsx27("div", { className: "absolute min-h-full top-0 left-0 right-0 bg-[#c6e0ff] z-10 flex justify-center items-center h-full w-full", children: /* @__PURE__ */ jsx27("h2", { className: "text-center text-[#0066ff]", children: "Drop files here" }) }),
          /* @__PURE__ */ jsxs16(
            "div",
            {
              id: "chat-messages-list",
              ref: chatListRef,
              className: "absolute inset-0 top-0 bottom-0 left-0 right-0 p-4 space-y-2 overflow-auto",
              children: [
                hasOlderMessages && !isLoadingOlder && /* @__PURE__ */ jsx27(Sentinal, { onInViewport: onFetchOlderMessages }),
                hasOlderMessages && isLoadingOlder && /* @__PURE__ */ jsx27(Spinner, { variant: "blue" }),
                groupedMessages.length ? groupedMessages.map(([date, daysMessages], i) => /* @__PURE__ */ jsxs16("div", { "data-chat-message-bubble": true, children: [
                  /* @__PURE__ */ jsx27(MessageDateBreak, { css: { mb: 24 }, dateString: date }),
                  daysMessages.map((messages2) => {
                    return /* @__PURE__ */ jsxs16("div", { children: [
                      messages2[0].id === firstUnreadId && /* @__PURE__ */ jsx27(
                        UnreadMessageBreak,
                        {
                          css: { mb: 24 },
                          unreadCount: pinnedUnreadCount || 0
                        }
                      ),
                      /* @__PURE__ */ jsx27(
                        ChatMessages,
                        {
                          messages: messages2,
                          workspace: chatWorkspace,
                          unreadId: firstUnreadId,
                          unreadCount: pinnedUnreadCount || 0,
                          user,
                          onRead: (id) => readMessage(id)
                        },
                        i
                      )
                    ] }, messages2[0].id);
                  })
                ] }, date + `_` + i)) : hasMessagesLoaded ? emptyState : null,
                hasNewerMessages && !isLoadingNewer && /* @__PURE__ */ jsx27(Sentinal, { onInViewport: onFetchNewerMessages }),
                hasNewerMessages && isLoadingNewer && /* @__PURE__ */ jsx27(Spinner, { variant: "blue" })
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx27(TypingStatusLabel, { statusInfo: typingStatus }),
    /* @__PURE__ */ jsx27(Footer, { className: "border-t", children: /* @__PURE__ */ jsxs16(
      "form",
      {
        ref: formRef,
        onSubmit: (e) => {
          e.preventDefault();
          clearAll();
          onSubmit(e);
          scrollToBottom();
        },
        children: [
          uploadState.filter((state2) => !!state2.result).map((state2, i) => /* @__PURE__ */ jsx27(
            "input",
            {
              type: "hidden",
              name: "attachmentId",
              value: state2.result?.id
            },
            i
          )),
          /* @__PURE__ */ jsxs16(
            "div",
            {
              className: `grid flex-1 grid-cols-[min-content_1fr_min-content] ${uploadState.length > 0 && "grid-rows-2 gap-4"} gap-x-4 p-4`,
              children: [
                uploadState.length > 0 && /* @__PURE__ */ jsx27(
                  Attachments,
                  {
                    className: "col-span-3",
                    files: uploadState,
                    removeUpload
                  }
                ),
                /* @__PURE__ */ jsx27("div", { className: "row-start-2 flex", children: /* @__PURE__ */ jsx27(
                  FileTrigger,
                  {
                    onSelect: (files) => checkAndUploadFiles(files, uploadFiles),
                    allowsMultiple: true,
                    children: /* @__PURE__ */ jsx27(Button2, { className: "bg-transparent h-12 self-end", children: /* @__PURE__ */ jsx27(PaperClipIcon, {}) })
                  }
                ) }),
                /* @__PURE__ */ jsx27(
                  AutoGrowTextArea,
                  {
                    autoFocus: true,
                    name: "content",
                    value: chat.draft,
                    placeholder: "Type your message...",
                    className: "row-start-2 px-4 py-3 min-h-12 rounded-2xl bg-[#F7F8F9] outline-[#2B6BD1] focus:shadow-[0px_0px_0px_4px_rgba(58,131,247,0.20)]",
                    onChange: onInputChange,
                    onKeyDown: (e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }
                  }
                ),
                /* @__PURE__ */ jsx27(
                  "button",
                  {
                    disabled: !chat.draft || showSendingSpinner,
                    className: "w-24 row-start-2 bg-[#3A83F7] disabled:bg-[#9EC8FF] disabled:text-[#E9F3FF] text-white rounded-2xl min-h-12 px-6 py-3 cursor-pointer max-h-12 self-end",
                    type: "submit",
                    children: showSendingSpinner ? /* @__PURE__ */ jsx27(Spinner, { variant: "white", css: { margin: "auto" } }) : "Send"
                  }
                )
              ]
            }
          )
        ]
      }
    ) })
  ] });
}

// src/components/ChatWindowWrapper.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
function ChatWindowWrapper({
  activeChat,
  showHeader = true,
  showBackButton = false,
  emptyState = null,
  onClickBack = () => {
  },
  beforeSendMessage,
  isLoading,
  page
}) {
  const appCtx = useChatAppContext();
  const { state } = appCtx;
  const { user } = state;
  const { sendMessageAndWait, setIsTyping, markMessageAsRead } = appCtx.gateway;
  const activeChatState = activeChat ? appCtx.state.chats[activeChat.id] : null;
  const hasAnyMessages = activeChatState?.messages !== void 0;
  if (activeChat && typeof window !== "undefined") {
    window.genChatMessages = async () => {
      await apiClient.chat.genChatMessages({ chatId: activeChat.id });
    };
  }
  const {
    fetchOlderMessages,
    fetchNewerMessages,
    hasOlderMessages,
    hasNewerMessages
  } = useChatMessagePagination(activeChat?.id);
  useEffect8(() => {
    if (!hasAnyMessages) {
      fetchOlderMessages();
    }
  }, [hasAnyMessages, activeChat?.id]);
  const sendMessage = async (workspaceId, messageChatId, content, attachmentIds) => {
    const _tempId = generateUUID();
    appCtx.dispatch({
      type: "SEND_MESSAGE" /* SEND_MESSAGE */,
      payload: {
        workspaceId,
        chatId: messageChatId,
        _tempId,
        message: { content, sender: user, attachmentIds }
      }
    });
    try {
      const res = await sendMessageAndWait(
        workspaceId,
        messageChatId,
        content,
        attachmentIds,
        page
      );
      if (res) {
        appCtx.dispatch({
          type: "SEND_MESSAGE_SUCCESS" /* SEND_MESSAGE_SUCCESS */,
          payload: {
            workspaceId,
            chatId: messageChatId,
            _tempId,
            message: {
              ...res.message,
              status: "sent" /* Sent */
            }
          }
        });
      }
    } catch (err) {
      const error = err;
      appCtx.dispatch({
        type: "SEND_MESSAGE_ERROR" /* SEND_MESSAGE_ERROR */,
        payload: { workspaceId, chatId: messageChatId, _tempId, error }
      });
      showToast3({
        title: "Couldn't send a message",
        description: error.message,
        variant: "danger"
      });
    }
  };
  const [showSendingSpinner, setShowSendingSpinner] = useState8(false);
  if (isLoading) {
    return /* @__PURE__ */ jsx28(Flex11, { css: { height: "100%", justifyContent: "center" }, children: /* @__PURE__ */ jsx28(Spinner2, {}) });
  }
  if (activeChat && appCtx.state.user) {
    return /* @__PURE__ */ jsx28(
      ChatWindow,
      {
        messages: activeChatState?.messages,
        hasMessagesLoaded: typeof activeChatState?.messages !== "undefined",
        markMessageAsRead,
        setIsTyping,
        chat: activeChat,
        unreadCount: activeChatState?.unreadCount,
        user: appCtx.state.user,
        onChange: (e) => {
          appCtx.dispatch({
            type: "SET_DRAFT" /* SET_DRAFT */,
            payload: {
              workspaceId: activeChat.workspaceId,
              chatId: activeChat.id,
              draft: e.target.value
            }
          });
        },
        showHeader,
        showBackButton,
        onClickBack,
        onSubmit: async (e) => {
          const form = e.target;
          const formData = new FormData(form);
          const input = form.content;
          const content = formData.get("content");
          const attachmentIds = formData.getAll("attachmentId");
          if (!content) {
            return;
          }
          if (beforeSendMessage) {
            setShowSendingSpinner(true);
            await beforeSendMessage();
            setShowSendingSpinner(false);
          }
          sendMessage(
            activeChat.workspaceId,
            activeChat.id,
            content,
            attachmentIds
          );
          input.focus();
        },
        emptyState,
        isLoadingOlder: activeChatState?.isLoadingOlder,
        isLoadingNewer: activeChatState?.isLoadingNewer,
        onFetchOlderMessages: fetchOlderMessages,
        onFetchNewerMessages: fetchNewerMessages,
        hasNewerMessages,
        hasOlderMessages,
        showSendingSpinner
      },
      activeChat.id
    );
  }
  if (emptyState) {
    return emptyState;
  }
  return null;
}

// src/components/ChatsList.tsx
import { Divider as Divider8, Flex as Flex12, Tab as Tab2, Tabs as Tabs2, TabsList as TabsList2 } from "@betterleap/ui";
import { AnimatePresence } from "framer-motion";

// src/components/ChatsListItem.tsx
import { cx } from "cva";
import { useInView } from "framer-motion";
import { format as format3, isThisWeek, isThisYear as isThisYear2, isToday as isToday2, isYesterday as isYesterday2 } from "date-fns";
import { motion as motion3 } from "framer-motion";
import { useEffect as useEffect10, useRef as useRef11 } from "react";
import removeMd from "remove-markdown";

// src/hooks/useFetchUnreadCount.ts
import { useEffect as useEffect9, useRef as useRef10 } from "react";
var useFetchChatMetadata = (chatId) => {
  const { state, dispatch } = useChatAppContext();
  const chatIdRef = useRef10(null);
  const chat = state.chats[chatId];
  const unreadCount = chat?.unreadCount || 0;
  const fetchChatMetadata = async (chatId2) => {
    const res = await apiClient.chat.getChatMetadata({ chatId: chatId2 });
    if (res.data) {
      dispatch({
        type: "SET_CHAT_METADATA" /* SET_CHAT_METADATA */,
        payload: {
          chatId: chatId2,
          metadata: res.data
        }
      });
    }
  };
  if (typeof window !== "undefined") {
    window.fetchChatMetadata = fetchChatMetadata;
  }
  useEffect9(() => {
    if (chatIdRef.current !== chatId) {
      chatIdRef.current = chatId;
      fetchChatMetadata(chatId);
    }
  }, [chatId]);
  return unreadCount;
};

// src/components/ChatsListItem.tsx
import { Avatar as Avatar8 } from "@betterleap/shared";
import { Fragment as Fragment7, jsx as jsx29, jsxs as jsxs17 } from "react/jsx-runtime";
function formatChatListTime(date) {
  if (isToday2(date)) {
    return format3(date, "h:mm a");
  } else if (isYesterday2(date)) {
    return "Yesterday";
  } else if (isThisWeek(date)) {
    return format3(date, "EEEE");
  } else if (isThisYear2(date)) {
    return format3(date, "MMMM d");
  } else {
    return format3(date, "MMMM d, yyyy");
  }
}
function ChatsListItem({
  chatState,
  user,
  isActive,
  onChatClick
}) {
  const appCtx = useChatAppContext();
  const listItemRef = useRef11(null);
  const inView = useInView(listItemRef, { once: true });
  const loadedLatestMessage = useRef11(false);
  const { chat, messages, unreadCount = 0 } = chatState;
  const { fetchOlderMessages } = useChatMessagePagination(chatState.chat.id);
  useFetchChatMetadata(chatState.chat.id);
  useEffect10(() => {
    if (!isActive && !messages && !loadedLatestMessage.current && inView) {
      fetchOlderMessages({ limit: 1 });
      loadedLatestMessage.current = true;
    }
  }, [isActive, messages, inView]);
  const lastMessage = messages?.[messages.length - 1];
  const lastSender = lastMessage?.sender;
  let lastSenderName = lastSender?.profile?.firstName ?? lastSender?.email;
  if (user.id === lastSender?.id) {
    lastSenderName = "You";
  }
  let contentPreview = "";
  if (!isActive && chat.draft) {
    contentPreview = `Draft: ${chat.draft}`;
  } else if (lastMessage) {
    contentPreview = `${lastSenderName ? `${lastSenderName}: ` : ""}${removeMd(
      lastMessage.content
    )}`;
  }
  const chatWorkspace = findWorkspaceById(appCtx.state, chat.workspaceId);
  let chatSubject = chat.subject ?? "Unknown";
  if (user.role === "candidate" && chatWorkspace) {
    chatSubject = chatWorkspace.name;
  }
  const avatar = getWorkspaceAvatar({
    workspace: chatWorkspace,
    user,
    subject: chat.subjectUser
  });
  return /* @__PURE__ */ jsx29(
    motion3.div,
    {
      ref: listItemRef,
      layout: true,
      transition: { type: "spring", stiffness: 500, damping: 30 },
      "data-unread": unreadCount,
      className: "group relative cursor-pointer border-[#E8EAED] p-1 min-h-20 transition-colors peer border-b",
      onClick: () => {
        onChatClick(getChatUrl(appCtx.state, chat.id));
      },
      children: /* @__PURE__ */ jsxs17(
        "div",
        {
          className: cx(
            "p-3 items-center flex gap-3 group-hover:bg-[#F7F8F9] rounded-xl",
            { "bg-blue-100": isActive }
          ),
          children: [
            /* @__PURE__ */ jsx29(Avatar8, { image: avatar, name: chatSubject, size: "lg" }),
            /* @__PURE__ */ jsxs17("div", { className: "flex flex-col min-w-0 flex-grow", children: [
              /* @__PURE__ */ jsxs17("div", { className: "flex justify-between mb-1", children: [
                /* @__PURE__ */ jsx29("p", { className: "flex-1 font-medium text-sm text-ellipsis whitespace-nowrap overflow-hidden", children: chatSubject }),
                lastMessage && /* @__PURE__ */ jsx29(
                  "span",
                  {
                    className: cx(
                      "text-[#87909E] text-sm whitespace-nowrap ml-auto",
                      {
                        "text-[#8B5CF6]": unreadCount > 0
                      }
                    ),
                    children: formatChatListTime(new Date(lastMessage.createdAt))
                  }
                )
              ] }),
              /* @__PURE__ */ jsx29("div", { className: "flex justify-between", children: contentPreview && /* @__PURE__ */ jsxs17(Fragment7, { children: [
                /* @__PURE__ */ jsx29("p", { className: "flex-1 text-[#4F5762] text-sm text-ellipsis whitespace-nowrap overflow-hidden", children: contentPreview }),
                unreadCount > 0 && /* @__PURE__ */ jsx29("div", { className: "w-[18px] h-[18px] bg-[#8B5CF6] rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-medium text-center", children: unreadCount })
              ] }) })
            ] })
          ]
        }
      )
    },
    chat.id
  );
}

// src/components/ChatsList.tsx
import { jsx as jsx30, jsxs as jsxs18 } from "react/jsx-runtime";
function ChatList({
  chatStates,
  user,
  activeChatId = null,
  onChatClick,
  onFilterChange,
  headerMenu,
  footerContent
}) {
  const newestFirst = sortChatStatesByMostRecent(chatStates);
  return /* @__PURE__ */ jsxs18(Container2, { children: [
    /* @__PURE__ */ jsxs18(Header2, { children: [
      /* @__PURE__ */ jsxs18(Flex12, { css: { px: 16, height: 64 }, justify: "between", children: [
        /* @__PURE__ */ jsx30("h2", { className: "text-lg font-medium", children: "Chat" }),
        headerMenu
      ] }),
      /* @__PURE__ */ jsx30(Divider8, { css: { backgroundColor: "$neutral-blue-300" } }),
      onFilterChange && /* @__PURE__ */ jsx30(
        Tabs2,
        {
          css: { px: 16, my: 16 },
          tabStyle: "pill",
          defaultValue: "all",
          onValueChange: (val) => onFilterChange?.(val),
          children: /* @__PURE__ */ jsxs18(TabsList2, { children: [
            /* @__PURE__ */ jsx30(Tab2, { onClick: () => null, value: "all", children: "All Messages" }),
            /* @__PURE__ */ jsx30(Tab2, { onClick: () => null, value: "replied", children: "Replied" }),
            /* @__PURE__ */ jsx30(Tab2, { onClick: () => null, value: "unread", children: "Unread" })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx30(List, { children: /* @__PURE__ */ jsx30(AnimatePresence, { children: newestFirst.map((chatState) => /* @__PURE__ */ jsx30(
      ChatsListItem,
      {
        chatState,
        user,
        isActive: activeChatId === chatState.chat.id,
        onChatClick
      },
      chatState.chat.id
    )) }) }),
    /* @__PURE__ */ jsx30(Footer, { children: footerContent })
  ] });
}

// src/components/NewChatMenu.tsx
import { useState as useState10 } from "react";
import { Button as Button4 } from "react-aria-components";

// src/components/AnnouncementModal.tsx
import { useState as useState9 } from "react";
import { Dialog, Heading, Modal as Modal2 } from "react-aria-components";
import {
  Button as Button3,
  CheckboxOption,
  Flex as Flex13,
  Icon as Icon7,
  Label,
  ListBox,
  Option,
  Text as Text11
} from "@betterleap/ui";
import { jsx as jsx31, jsxs as jsxs19 } from "react/jsx-runtime";
function AnnouncementModal({
  workspace,
  isOpen,
  setOpen
}) {
  const { state: appState, showGlobalError, showToast: showToast4 } = useChatAppContext();
  const [content, setContent] = useState9("");
  const [sending, setSending] = useState9(false);
  const [error, setError] = useState9(null);
  const [selectedKeys, setSelectedKeys] = useState9("all");
  const users = new Map(
    workspace?.members?.filter((m) => m.id !== appState.user?.id && m.role === "candidate").map((m) => [m.id, m])
  );
  const selectedUsers = selectedKeys === "all" ? null : selectedKeys.map((k) => users.has(k) && users.get(k)).filter((v) => {
    return Boolean(v);
  });
  const sendAnnouncement = async () => {
    setSending(false);
    setError(null);
    if (content.trim().length === 0) {
      setError("Message cannot be empty");
      return;
    }
    const userIds = selectedKeys === "all" ? Array.from(users.keys()) : selectedUsers.map((u) => u.id);
    if (!userIds || userIds.length === 0) {
      setError("No users selected");
      return;
    }
    setSending(true);
    try {
      await apiClient.chatWorkspace.createAnnouncement({
        idOrSlug: workspace.id,
        requestBody: {
          userIds,
          content
        }
      });
      showToast4("Successfully sent announcement");
      setOpen(false);
    } catch (e) {
      showGlobalError("Failed to send announcement");
      setError("Failed to send announcement");
    } finally {
      setSending(false);
    }
  };
  const hasSelectedUsers = selectedKeys === "all" || selectedUsers && selectedUsers?.length > 0;
  const canSend = hasSelectedUsers && !sending;
  return /* @__PURE__ */ jsx31(
    Modal2,
    {
      isDismissable: true,
      isOpen,
      onOpenChange: setOpen,
      className: "fixed h-screen w-screen inset-0 flex items-center justify-center bg-black/50 z-50 overflow-auto p-6",
      children: /* @__PURE__ */ jsxs19(Dialog, { className: "p-8 rounded-xl bg-white flex flex-col gap-6 min-w-[44rem]", children: [
        /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx31(
            Heading,
            {
              slot: "title",
              className: "font-medium flex justify-center mb-2 text-center text-gray-900",
              children: "Send Announcement"
            }
          ),
          /* @__PURE__ */ jsx31(Text11, { align: "center", size: "sm", css: { color: "$gray-500" }, children: "Select candidates to share your announcement." })
        ] }),
        /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsxs19(Flex13, { css: { mb: 8 }, justify: "between", children: [
            /* @__PURE__ */ jsx31(Label, { css: { fontSize: "$sm", color: "$text-secondary" }, children: "Candidates" }),
            /* @__PURE__ */ jsxs19(
              Button3,
              {
                variant: "link",
                onClick: () => setSelectedKeys(selectedKeys === "all" ? [] : "all"),
                children: [
                  /* @__PURE__ */ jsx31(Icon7, { inherit: true, name: "check", size: 16, css: { mr: 8 } }),
                  "Select all"
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsx31("div", { className: "relative", children: /* @__PURE__ */ jsx31(
            ListBox,
            {
              css: {
                maxHeight: 256,
                pb: 70,
                after: {
                  content: "",
                  display: "block",
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "70px",
                  background: "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)",
                  pointerEvents: "none"
                }
              },
              value: selectedKeys,
              onChange: (newKeys) => {
                setSelectedKeys(newKeys);
              },
              multi: true,
              children: Array.from(users.values()).map((user) => /* @__PURE__ */ jsx31(
                Option,
                {
                  css: {
                    mb: 4,
                    py: 12,
                    selected: {
                      backgroundColor: "$blue-100"
                    }
                  },
                  component: /* @__PURE__ */ jsx31(CheckboxOption, {}),
                  id: user.id,
                  children: user.email || "Unknown user"
                },
                user.id
              ))
            }
          ) })
        ] }),
        /* @__PURE__ */ jsxs19("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx31(Label, { css: { fontSize: "$sm", color: "$text-secondary", mb: 8 }, children: "Announcement" }),
          /* @__PURE__ */ jsx31(
            AutoGrowTextArea,
            {
              autoFocus: true,
              style: { minHeight: 84 },
              value: content,
              onChange: (e) => setContent(e.target.value),
              disabled: sending,
              placeholder: "Type a message...",
              className: "border border-[#E8EAED] px-4 py-3 max-h-96 w-full rounded-xl"
            }
          )
        ] }),
        error && /* @__PURE__ */ jsx31("p", { className: "text-red-600", children: error }),
        /* @__PURE__ */ jsxs19(Flex13, { css: { gap: 8 }, children: [
          /* @__PURE__ */ jsx31(
            Button3,
            {
              variant: "gray",
              onClick: () => setOpen(false),
              css: { flex: 1 },
              disabled: sending,
              children: "Cancel"
            }
          ),
          /* @__PURE__ */ jsx31(
            Button3,
            {
              css: { flex: 1 },
              disabled: !canSend,
              onClick: () => sendAnnouncement(),
              children: "Send"
            }
          )
        ] })
      ] })
    }
  );
}

// src/components/NewChatMenu.tsx
import { PlusIcon } from "@heroicons/react/24/outline";
import { Fragment as Fragment8, jsx as jsx32, jsxs as jsxs20 } from "react/jsx-runtime";
function NewChatMenu({
  workspace,
  className
}) {
  const [announcementOpen, setAnnouncementOpen] = useState10(false);
  return /* @__PURE__ */ jsxs20(Fragment8, { children: [
    /* @__PURE__ */ jsxs20(
      Button4,
      {
        "aria-label": "New Chat Menu",
        onPress: () => {
          setAnnouncementOpen(true);
        },
        className: "p-1 px-3 text-sm flex items-center text-[#3A83F7] bg-white border-none whitespace-nowrap flex-shrink-0" + (className ? ` ${className}` : ""),
        children: [
          /* @__PURE__ */ jsx32(
            PlusIcon,
            {
              className: "w-4 h-4 inline-block mr-2",
              style: { fill: "currentColor" }
            }
          ),
          "New Announcement"
        ]
      }
    ),
    /* @__PURE__ */ jsx32(
      AnnouncementModal,
      {
        workspace,
        isOpen: announcementOpen,
        setOpen: setAnnouncementOpen
      }
    )
  ] });
}

// src/components/OrganizationLinkModal.tsx
import { useState as useState11, useRef as useRef12 } from "react";

// src/components/BetterleapLogo.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var BetterleapLogo = (props) => /* @__PURE__ */ jsx33(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 41,
    height: 41,
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx33(
      "path",
      {
        fill: "#C4B5FD",
        d: "M21.606 11.07H19.69L15.29.82 17.164 0l3.464 8.077L24.09 0l1.915.82zM24.091 41l-3.463-8.036L17.164 41l-1.874-.82 4.4-10.25h1.915l4.401 10.25zM13.334 14.513l-10.309-4.1.774-1.927 8.109 3.198-3.22-8.118 1.916-.779 4.075 10.373zm17.358 23.699-4.075-10.373 1.345-1.312 10.309 4.1-.774 1.927-8.109-3.24 3.219 8.16zM1.109 25.87l-.815-1.885L8.281 20.5.293 17.015l.815-1.886 10.146 4.428v1.886zm39.076 0L30.04 21.444v-1.886l10.146-4.428.815 1.886-7.986 3.485L41 23.985zM10.604 38.212l-1.915-.738 3.219-8.16-8.109 3.24-.774-1.927 10.309-4.1 1.345 1.312zm17.358-23.699-1.345-1.353 4.075-10.373 1.915.78-3.219 8.117 8.109-3.198.774 1.927z"
      }
    )
  }
);
var BetterleapLogo_default = BetterleapLogo;

// src/components/OrganizationLinkModal.tsx
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { jsx as jsx34, jsxs as jsxs21 } from "react/jsx-runtime";
function FauxModal({
  className,
  children
}) {
  return /* @__PURE__ */ jsx34(
    "div",
    {
      className: className + " min-h-screen flex items-center justify-center",
      children: /* @__PURE__ */ jsx34("div", { className: "bg-white p-12 rounded-2xl shadow-md w-full mx-auto max-w-md relative overflow-hidden", children })
    }
  );
}
var OrganizationLinkModal = ({
  workspaceName,
  url,
  imageUrl
}) => {
  const ref2 = useRef12(null);
  const [copied, setCopied] = useState11(false);
  function reset() {
    setTimeout(() => {
      setCopied(false);
    }, 2e3);
  }
  function copyLink() {
    if (!navigator.clipboard) {
      if (ref2.current) {
        ref2.current.select();
        document.execCommand("copy");
      }
    } else {
      navigator.clipboard.writeText(url);
    }
    setCopied(true);
    reset();
  }
  return /* @__PURE__ */ jsxs21(FauxModal, { children: [
    /* @__PURE__ */ jsxs21("div", { className: "text-center flex flex-col gap-2", children: [
      imageUrl ? /* @__PURE__ */ jsx34("img", { src: imageUrl, className: "max-w-full h-16 mx-auto" }) : /* @__PURE__ */ jsx34(BetterleapLogo_default, { className: "h-10 w-10 mx-auto" }),
      /* @__PURE__ */ jsxs21("h1", { className: "text-2xl font-bold text-center font-alt", children: [
        workspaceName,
        " Get Connected!"
      ] })
    ] }),
    /* @__PURE__ */ jsxs21("p", { className: "text-sm text-alabaster-500 mt-4 mb-6 text-center", children: [
      "Share this link with candidates to get connected",
      " "
    ] }),
    /* @__PURE__ */ jsxs21("div", { className: "flex items-center focus-within:outline-purple-500", children: [
      /* @__PURE__ */ jsx34(
        "input",
        {
          readOnly: true,
          className: "w-full border-gray-300 border py-2 pl-4 pr-0 rounded-l-lg outline-none",
          ref: ref2,
          value: url,
          onClick: (e) => {
            e.currentTarget.select();
          }
        }
      ),
      /* @__PURE__ */ jsx34(
        "button",
        {
          onClick: () => copyLink(),
          className: "text-purple-500 bg-white px-3 py-2 rounded-r-lg border border-gray-300 hover:border-purple-500 hover:bg-purple-500 hover:text-white transition-colors duration-200 ease-in-out whitespace-nowrap border-l-0",
          children: copied ? "Copied!" : "Copy"
        }
      )
    ] })
  ] });
};
var OrganizationLinkCard = ({
  workspaceName,
  url,
  imageUrl
}) => {
  const ref2 = useRef12(null);
  const [copied, setCopied] = useState11(false);
  function reset() {
    setTimeout(() => {
      setCopied(false);
    }, 2e3);
  }
  function copyLink() {
    if (!navigator.clipboard) {
      if (ref2.current) {
        ref2.current.select();
        document.execCommand("copy");
      }
    } else {
      navigator.clipboard.writeText(url);
    }
    setCopied(true);
    reset();
  }
  return /* @__PURE__ */ jsx34("div", { className: "bg-white rounded-2xl shadow-md p-4 border border-gray-100 w-full", children: /* @__PURE__ */ jsxs21("div", { children: [
    /* @__PURE__ */ jsxs21("div", { className: "mb-4", children: [
      imageUrl ? /* @__PURE__ */ jsx34("img", { src: imageUrl, className: "max-w-full h-5" }) : null,
      /* @__PURE__ */ jsx34("h2", { className: "text-base font-medium mb-2", children: workspaceName }),
      /* @__PURE__ */ jsx34("p", { className: "text-[#656F7D] text-xs", children: "Get connected. Share this link:" })
    ] }),
    /* @__PURE__ */ jsxs21("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx34(
        "input",
        {
          readOnly: true,
          className: "w-full py-2 px-3 bg-[#F7F8F9] text-sm rounded-xl outline-none ",
          ref: ref2,
          value: url,
          onClick: (e) => {
            e.currentTarget.select();
          }
        }
      ),
      /* @__PURE__ */ jsxs21(
        "button",
        {
          onClick: () => copyLink(),
          className: "flex items-center text-[#3A83F7] bg-[#E9F3FF] px-3 py-2 rounded-xl border border-[#E9F3FF] hover:border-[#C3DEFF] hover:bg-[#C3DEFF] transition-colors duration-200 ease-in-out text-sm whitespace-nowrap",
          children: [
            /* @__PURE__ */ jsx34(DocumentDuplicateIcon, { className: "stroke-text-[#3A83F7] h-4 w-4 mr-2" }),
            copied ? "Copied!" : "Copy"
          ]
        }
      )
    ] })
  ] }) });
};

// src/components/RecruiterChat.layout.tsx
import { Box as Box8 } from "@betterleap/ui";
import { jsx as jsx35 } from "react/jsx-runtime";
var RecruiterChatLayout = ({ css, ...props }) => /* @__PURE__ */ jsx35(
  Box8,
  {
    css: {
      display: "grid",
      gridTemplateAreas: `
          "list chat profile"
          "list chat profile"
        `,
      gridTemplateRows: "1fr 1fr",
      gridTemplateColumns: "1fr 2fr 1fr",
      height: "100%",
      ...css
    },
    ...props
  }
);
RecruiterChatLayout.List = function List2({ css, ...props }) {
  return /* @__PURE__ */ jsx35(
    Box8,
    {
      css: {
        gridArea: "list",
        backgroundColor: "$white",
        borderRight: "1px solid $neutral-blue-300",
        ...css
      },
      ...props
    }
  );
};
RecruiterChatLayout.Chat = function Chat({ css, ...props }) {
  return /* @__PURE__ */ jsx35(
    Box8,
    {
      css: { gridArea: "chat", backgroundColor: "$white", ...css },
      ...props
    }
  );
};
RecruiterChatLayout.Profile = function Profile({ css, ...props }) {
  return /* @__PURE__ */ jsx35(
    Box8,
    {
      css: {
        gridArea: "profile",
        backgroundColor: "$white",
        borderLeft: "1px solid $neutral-blue-300",
        ...css
      },
      ...props
    }
  );
};

// src/components/RecruiterChatView.tsx
import { jsx as jsx36, jsxs as jsxs22 } from "react/jsx-runtime";
function RecruiterChatView({
  url,
  emptyState,
  onChatClick,
  onClickBack
}) {
  const appCtx = useChatAppContext();
  const { workspaces } = appCtx.state;
  const { chatId } = useParams2();
  const [inboxType, setInboxType] = useState12(
    "all"
  );
  const defaultWorkspace = workspaces[0];
  const isMobile = false;
  const navigate = useNavigate();
  const allChatStates = Object.values(appCtx.state.chats);
  const chats = sortChatsByMostRecent(allChatsSelector(appCtx.state));
  const replied = sortChatStatesByMostRecent(
    repliedChatsSelector(allChatStates)
  );
  const unread = allChatStates.filter(
    (c) => c.unreadCount && c.unreadCount > 0
  );
  useEffect11(() => {
    if (!chatId && chats.length > 0) {
      navigate(getChatUrl(appCtx.state, chats[0].id));
    }
  }, [chatId, chats]);
  if (appCtx.state.authState === "error") {
    return /* @__PURE__ */ jsx36("div", { className: "h-full grid place-items-center p-6", children: /* @__PURE__ */ jsx36("div", { className: "text-center text-gray-600 max-w-md", children: "Unable to initialize chat. Please refresh the page and try again." }) });
  }
  if (!appCtx.state.user) {
    return /* @__PURE__ */ jsx36("div", { className: "h-full grid place-items-center p-6", children: /* @__PURE__ */ jsx36("div", { className: "text-gray-600", children: "Loading chat..." }) });
  }
  if (!defaultWorkspace) {
    return /* @__PURE__ */ jsx36("div", { className: "h-full grid place-items-center p-6", children: /* @__PURE__ */ jsx36("div", { className: "text-center text-gray-600 max-w-md", children: "No chat workspace is available for this account." }) });
  }
  let selectedChats = [];
  switch (inboxType) {
    case "all":
      selectedChats = allChatStates;
      break;
    case "replied":
      selectedChats = replied;
      break;
    case "unread":
      selectedChats = unread;
      break;
  }
  selectedChats = selectedChats.filter((chat) => !chat.chat.isPreChat);
  const chatList = /* @__PURE__ */ jsx36(
    ChatList,
    {
      chatStates: selectedChats,
      user: appCtx.state.user,
      activeChatId: chatId,
      onChatClick,
      onFilterChange: (filter) => setInboxType(filter),
      headerMenu: /* @__PURE__ */ jsx36(NewChatMenu, { workspace: defaultWorkspace, className: "ml-auto" }),
      footerContent: /* @__PURE__ */ jsx36("div", { className: "p-4", children: /* @__PURE__ */ jsx36(
        OrganizationLinkCard,
        {
          workspaceName: defaultWorkspace.name,
          url
        }
      ) })
    }
  );
  if (chats.length === 0) {
    return /* @__PURE__ */ jsx36(
      OrganizationLinkModal,
      {
        workspaceName: defaultWorkspace.name,
        url,
        imageUrl: defaultWorkspace.imageUrl
      }
    );
  }
  const activeChat = chats.find((chat) => chat.id === chatId);
  const chatWindow = /* @__PURE__ */ jsx36(
    ChatWindowWrapper,
    {
      activeChat,
      emptyState,
      showBackButton: false,
      onClickBack
    }
  );
  if (isMobile) {
    return /* @__PURE__ */ jsx36("div", { className: "bg-white h-dvh", children: chatId ? chatWindow : chatList });
  }
  return /* @__PURE__ */ jsxs22(RecruiterChatLayout, { children: [
    /* @__PURE__ */ jsx36(RecruiterChatLayout.List, { children: chatList }),
    /* @__PURE__ */ jsx36(RecruiterChatLayout.Chat, { children: chatWindow }),
    /* @__PURE__ */ jsx36(RecruiterChatLayout.Profile, { children: /* @__PURE__ */ jsx36(CandidateProfileSidebar, {}) })
  ] });
}

// src/components/CandidateChatView.tsx
import { useEffect as useEffect12 } from "react";

// src/components/CandidateChat.layout.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var Container3 = ({ children, ...props }) => /* @__PURE__ */ jsx37(
  "div",
  {
    style: {
      gridTemplateColumns: "1fr 2fr"
    },
    className: "bg-white h-full grid [grid-template-areas:'chat_chat'] md:[grid-template-areas:'chatList_chat'] [grid-template-rows:1fr]",
    ...props,
    children
  }
);
var ChatList2 = ({ children, ...props }) => /* @__PURE__ */ jsx37("div", { style: { gridArea: "chatList" }, ...props, children });
var Chat2 = ({ children, ...props }) => /* @__PURE__ */ jsx37("div", { style: { gridArea: "chat" }, ...props, children });

// src/components/CandidateChatView.tsx
import { useIsMobile } from "@betterleap/ui";
import { Fragment as Fragment9, jsx as jsx38, jsxs as jsxs23 } from "react/jsx-runtime";
function CandidateChatView({
  chatId,
  emptyState,
  chatListFooter,
  onChatClick,
  onClickBack
}) {
  const appCtx = useChatAppContext();
  const isMobile = useIsMobile();
  const chatStates = allChatStatesSelector(appCtx.state);
  const betterleapWorkspace = appCtx.state.workspaces.find(
    (w) => w.slug === "betterleap"
  );
  const betterleapChat = chatStates.find(
    (c) => c.chat.workspaceId === betterleapWorkspace?.id
  );
  useEffect12(() => {
    if (!isMobile && !chatId && betterleapChat) {
      onChatClick(getChatUrl(appCtx.state, betterleapChat.chat.id));
    }
  }, [chatId, betterleapChat]);
  if (!appCtx.state.user) {
    return /* @__PURE__ */ jsx38("div", { children: "Loading..." });
  }
  const chatList = /* @__PURE__ */ jsx38(
    ChatList,
    {
      chatStates,
      user: appCtx.state.user,
      activeChatId: chatId,
      onChatClick,
      footerContent: chatListFooter
    }
  );
  const activeChatState = chatStates.find((c) => c.chat.id === chatId);
  const chatWindow = /* @__PURE__ */ jsx38(
    ChatWindowWrapper,
    {
      activeChat: activeChatState?.chat,
      emptyState,
      showBackButton: isMobile,
      onClickBack
    }
  );
  let content = null;
  if (isMobile) {
    content = /* @__PURE__ */ jsx38(Chat2, { className: "overflow-hidden", children: chatId ? chatWindow : chatList });
  } else {
    content = /* @__PURE__ */ jsxs23(Fragment9, { children: [
      /* @__PURE__ */ jsx38(ChatList2, { className: "border-r border-[#E8EAED]", children: chatList }),
      /* @__PURE__ */ jsx38(Chat2, { className: "overflow-hidden", children: chatWindow })
    ] });
  }
  return /* @__PURE__ */ jsx38(Container3, { children: content });
}

// src/components/EmptyStateSvg.tsx
import { jsx as jsx39, jsxs as jsxs24 } from "react/jsx-runtime";
var EmptyStateSVG = (props) => /* @__PURE__ */ jsxs24(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 290,
    height: 226,
    fill: "none",
    ...props,
    children: [
      /* @__PURE__ */ jsx39(
        "path",
        {
          fill: "url(#prefix__a)",
          fillRule: "evenodd",
          d: "M250.843 153.609c-6.859 20.607-35.551 28.416-56.585 41.486-19.332 12.013-34.558 30.233-59.507 30.653-26.678.448-49.811-13.537-69.28-28.487-21.742-16.695-46.385-35.682-44.896-59.187 1.49-23.538 25.317-43.078 52.002-53.015 22.13-8.242 46.43 7.359 71.184 7.141 30.689-.27 62.352-23.012 87.592-8.537 25.636 14.702 27.364 46.293 19.49 69.946",
          clipRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx39(
        "path",
        {
          fill: "#D6D9DE",
          d: "M264.517 72.658c.025-.211.332-.211.357 0l.211 1.776a4.996 4.996 0 0 0 4.372 4.372l1.776.21c.211.026.211.333 0 .358l-1.776.21a4.996 4.996 0 0 0-4.372 4.373l-.211 1.775c-.025.212-.332.212-.357 0l-.211-1.775a4.995 4.995 0 0 0-4.372-4.372l-1.775-.211c-.212-.025-.212-.332 0-.358l1.775-.21a4.995 4.995 0 0 0 4.372-4.372zM167.517.658c.025-.211.332-.211.357 0l.211 1.776a4.996 4.996 0 0 0 4.372 4.372l1.776.21c.211.026.211.333 0 .358l-1.776.21a4.996 4.996 0 0 0-4.372 4.373l-.211 1.775c-.025.212-.332.212-.357 0l-.211-1.775a4.995 4.995 0 0 0-4.372-4.372l-1.775-.211c-.212-.025-.212-.332 0-.357l1.775-.211a4.995 4.995 0 0 0 4.372-4.372z"
        }
      ),
      /* @__PURE__ */ jsx39(
        "path",
        {
          stroke: "#D6D9DE",
          strokeLinecap: "round",
          strokeWidth: 2.435,
          d: "M70.783 34.408H60.217M72.565 28.777l-9.29-5.032M77.037 25.544l-4.812-9.406"
        }
      ),
      /* @__PURE__ */ jsx39(
        "circle",
        {
          cx: 195.174,
          cy: 20.673,
          r: 3.957,
          stroke: "#D6D9DE",
          strokeWidth: 2.435
        }
      ),
      /* @__PURE__ */ jsx39(
        "path",
        {
          stroke: "#D6D9DE",
          strokeWidth: 2.435,
          d: "M41.13 107.369c0 1.949-1.7 3.652-3.956 3.652s-3.957-1.703-3.957-3.652c0-1.948 1.701-3.652 3.957-3.652s3.956 1.704 3.956 3.652Z"
        }
      ),
      /* @__PURE__ */ jsx39(
        "circle",
        {
          cx: 230.174,
          cy: 102.673,
          r: 3.957,
          stroke: "#D6D9DE",
          strokeWidth: 2.435
        }
      ),
      /* @__PURE__ */ jsx39(
        "path",
        {
          fill: "#D6D9DE",
          d: "M58.517 112.658c.025-.211.332-.211.357 0l.211 1.776a4.996 4.996 0 0 0 4.372 4.372l1.776.211c.211.025.211.332 0 .357l-1.776.211a4.995 4.995 0 0 0-4.372 4.372l-.21 1.775c-.026.212-.333.212-.358 0l-.21-1.775a4.995 4.995 0 0 0-4.373-4.372l-1.775-.211c-.212-.025-.212-.332 0-.357l1.775-.211a4.996 4.996 0 0 0 4.372-4.372z"
        }
      ),
      /* @__PURE__ */ jsxs24("g", { clipPath: "url(#prefix__b)", children: [
        /* @__PURE__ */ jsx39(
          "path",
          {
            fill: "#F0F1F3",
            stroke: "#fff",
            strokeWidth: 2,
            d: "m201.891 125.382 2.475 10.503-18.802-11.583-.241-.149h-54.794c-2.925 0-5.869-1.331-8.09-3.372-2.228-2.047-3.615-4.699-3.615-7.233v-.995h70.291c3.943 0 6.96-1.614 8.969-4.15 1.986-2.508 2.938-5.854 2.938-9.294V58.15h2.643c2.986 0 5.02.962 6.323 2.469 1.324 1.53 2.012 3.757 2.012 6.492v46.437c0 2.744-.691 5.428-2.074 7.397-1.357 1.932-3.395 3.208-6.261 3.208h-2.064z"
          }
        ),
        /* @__PURE__ */ jsx39(
          "path",
          {
            fill: "#E8EAED",
            stroke: "#fff",
            strokeWidth: 2,
            d: "m94.887 105.187.283-1.126-1.156-.113c-3.247-.318-6.552-1.515-9.02-3.322C82.52 98.816 81 96.491 81 93.908V41.319c0-2.856 1.857-5.931 4.744-8.344 2.871-2.4 6.569-3.975 9.925-3.975h82.8c3.335 0 6.598 1.558 9.04 3.926 2.448 2.373 3.97 5.458 3.97 8.393v52.592c0 2.892-1.483 5.409-3.88 7.234-2.405 1.831-5.691 2.926-9.13 2.926h-60.857l-.242.149-26.32 16.221zm4.936-40.632c0 4.836 3.753 8.837 8.48 8.837 4.721 0 8.479-4 8.479-8.837s-3.753-8.838-8.479-8.838c-4.727 0-8.48 4.001-8.48 8.838Zm27.937 0c0 4.836 3.753 8.837 8.479 8.837 4.727 0 8.48-4.001 8.48-8.837 0-4.837-3.753-8.838-8.48-8.838-4.726 0-8.479 4.001-8.479 8.838Zm27.94 0c0 4.837 3.758 8.837 8.484 8.837 4.718 0 8.48-4 8.48-8.837s-3.753-8.838-8.48-8.838c-4.726 0-8.484 4-8.484 8.838Z"
          }
        ),
        /* @__PURE__ */ jsx39("circle", { cx: 108.5, cy: 64.5, r: 8.5, fill: "#fff" }),
        /* @__PURE__ */ jsx39("circle", { cx: 136.5, cy: 64.5, r: 8.5, fill: "#fff" }),
        /* @__PURE__ */ jsx39("circle", { cx: 164.5, cy: 64.5, r: 8.5, fill: "#fff" })
      ] }),
      /* @__PURE__ */ jsxs24("defs", { children: [
        /* @__PURE__ */ jsxs24(
          "linearGradient",
          {
            id: "prefix__a",
            x1: 159.857,
            x2: 155.894,
            y1: 185.896,
            y2: 98.377,
            gradientUnits: "userSpaceOnUse",
            children: [
              /* @__PURE__ */ jsx39("stop", { stopColor: "#ECE7E7", stopOpacity: 0 }),
              /* @__PURE__ */ jsx39("stop", { offset: 1, stopColor: "#fff" })
            ]
          }
        ),
        /* @__PURE__ */ jsx39("clipPath", { id: "prefix__b", children: /* @__PURE__ */ jsx39("path", { fill: "#fff", d: "M80 28h133v110H80z" }) })
      ] })
    ]
  }
);
export {
  ActionType,
  CandidateChatView,
  CandidateCredentials,
  CandidateEducation,
  CandidateExperience,
  ChatAppContextProvider,
  ChatList,
  ChatMessageBubble,
  ChatMessageStatus,
  ChatWindow,
  ChatWindowWrapper,
  CredentialEntry,
  EducationEntry,
  EmptyStateSVG,
  ExperienceEntry,
  RecruiterChatView,
  allChatStatesSelector,
  allChatsFromWorkspacesSelector,
  allChatsSelector,
  apiClient,
  apiClientConfig,
  authStateSelector,
  chatHasRecruiterReply,
  chatSelector,
  constructFullName,
  externalWorkspacesSelector,
  findWorkspaceById,
  formatLocation,
  formatSummary,
  getChatUrl,
  getWorkspaceChat,
  mapChatSubject,
  profileDisplayName,
  profileLocation,
  repliedChatsSelector,
  unreadChatsCount,
  unreadChatsSelector,
  unreadMessagesCount,
  unrepliedChatsSelector,
  useChatAppContext,
  useChatState,
  workspaceHasMember
};
